import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Users,
    Building2,
    Camera,
    Check,
    CheckCircle2,
    Clock,
    Coffee,
    Download,
    ExternalLink,
    Search,
    FileSpreadsheet,
    Edit2,
    AlertCircle,
    Mail,
    MapPin,
    Plus,
    Undo2,
    ChevronDown,
    X,
} from "lucide-react";

import {
    useNavigate,
} from "react-router-dom";

import * as XLSX from "xlsx-js-style";
import { toast } from "react-hot-toast";

import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";
import ReverseGeocode from "../../components/ReverseGeocode";
import {
    createIndiaDateTimeIso,
    formatAttendanceRecordTime,
    getAutoCheckoutDisplayTime,
    getWorkDurationLabel,
    isAutoCheckoutRecord,
} from "../../utils/attendanceTime";

const LUNCH_LIMIT_MINUTES = 45;
const TIME_12_HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
const TIME_MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));

const parseTimeParts = (timeStr = "") => {
    const match = String(timeStr || "").trim().toUpperCase().match(/^(0?[1-9]|1[0-2]):([0-5][0-9])\s*(AM|PM)$/);
    if (match) return { h: match[1].padStart(2, "0"), m: match[2], a: match[3] };
    return { h: "", m: "", a: "AM" };
};

const getManualTimeMinutes = (timeStr = "") => {
    const { h, m, a } = parseTimeParts(timeStr);
    if (!h || !m) return null;
    let hours = Number(h);
    if (a === "PM" && hours < 12) hours += 12;
    if (a === "AM" && hours === 12) hours = 0;
    return (hours * 60) + Number(m);
};

const CustomTimePicker = ({ value, onChange }) => {
    const { h, m, a } = parseTimeParts(value);

    const updateTime = (newH, newM, newA) => {
        onChange({ target: { value: newH ? `${newH}:${newM || "00"} ${newA}` : "" } });
    };

    const selectClass = "h-11 min-w-0 border-0 bg-transparent px-2 text-sm font-bold text-neutral-800 outline-none dark:text-neutral-100";

    return (
        <div className="attendance-time-input grid w-full max-w-full grid-cols-[50px_16px_50px_64px] items-center overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] focus-within:border-[var(--accent-primary)] sm:w-max">
            <select value={h} onChange={(event) => updateTime(event.target.value, m, a)} className={selectClass} aria-label="Hour">
                <option value="">HH</option>
                {TIME_12_HOURS.map((hour) => <option key={hour} value={hour}>{hour}</option>)}
            </select>
            <span className="grid h-11 place-items-center border-l border-neutral-200 text-sm font-black text-neutral-500 dark:border-neutral-800">:</span>
            <select value={m} onChange={(event) => updateTime(h, event.target.value, a)} className={selectClass} aria-label="Minute">
                <option value="">MM</option>
                {TIME_MINUTES.map((minute) => <option key={minute} value={minute}>{minute}</option>)}
            </select>
            <select value={a} onChange={(event) => updateTime(h, m, event.target.value)} className={`${selectClass} border-l border-neutral-200 dark:border-neutral-800`} aria-label="AM or PM">
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </select>
        </div>
    );
};

const getAttendanceDateKey = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);

    const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmy) {
        const [, day, month, year] = dmy;
        return `${year}-${month}-${day}`;
    }

    const slashDmy = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashDmy) {
        const [, first, second, year] = slashDmy;
        const firstNumber = Number(first);
        const secondNumber = Number(second);
        const isMonthFirst = firstNumber <= 12 && secondNumber > 12;
        const day = String(isMonthFirst ? second : first).padStart(2, "0");
        const month = String(isMonthFirst ? first : second).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const formatTeamName = (value) => {
    const team = String(value || "").trim();
    if (!team) return "-";
    return `${team.charAt(0).toUpperCase()}${team.slice(1)}`;
};

const getEscalationHistoryCount = (employee = {}) =>
    Number(
        employee.escalationSummary?.historicalEscalationCount
        ?? employee.escalationSummary?.mailedEscalationCount
        ?? employee.historicalEscalationCount
        ?? employee.mailedEscalationCount
        ?? 0
    ) || 0;

const getEscalationTitle = (employee = {}) => {
    const count = getEscalationHistoryCount(employee);
    return count > 0
        ? `${count} escalation${count === 1 ? "" : "s"} received in history`
        : "No escalation history";
};

const EscalationBadge = ({ employee, compact = false }) => {
    const count = getEscalationHistoryCount(employee);
    const hasHistory = count > 0;
    return (
        <span
            title={getEscalationTitle(employee)}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black uppercase ${
                hasHistory
                    ? "bg-red-500/10 text-red-500 ring-1 ring-red-500/20"
                    : "bg-neutral-500/10 text-[var(--text-secondary)] ring-1 ring-neutral-500/10"
            }`}
        >
            <AlertCircle size={compact ? 11 : 12} />
            {compact ? count : `History ${count}`}
        </span>
    );
};

const AttendanceReport = () => {

    const navigate =
        useNavigate();
    const attendanceTopScrollRef = useRef(null);
    const attendanceTableScrollRef = useRef(null);
    const attendanceTableRef = useRef(null);

    const today =
        new Date();

    const formatISO =
        (d) =>
            `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

    const [
        records,
        setRecords,
    ] = useState([]);

    const [
        serverAbsentEmployeeDays,
        setServerAbsentEmployeeDays,
    ] = useState([]);
    const [
        serverApprovedLeaveEmployeeDays,
        setServerApprovedLeaveEmployeeDays,
    ] = useState([]);

    const [
        employees,
        setEmployees,
    ] = useState([]);

    const [
        attendanceLoading,
        setAttendanceLoading,
    ] = useState(true);

    const [
        ,
        setEmployeesLoading,
    ] = useState(true);

    const [
        selectedFromDate,
        setSelectedFromDate,
    ] = useState(
        formatISO(today),
    );

    const [
        selectedToDate,
        setSelectedToDate,
    ] = useState(
        formatISO(today),
    );

    const attendanceRange = useMemo(() => {
        const from = selectedFromDate || selectedToDate || formatISO(new Date());
        const to = selectedToDate || from;
        return from <= to
            ? { from, to }
            : { from: to, to: from };
    }, [selectedFromDate, selectedToDate]);

    const attendanceRangeFileKey = attendanceRange.from === attendanceRange.to
        ? attendanceRange.from
        : `${attendanceRange.from}_to_${attendanceRange.to}`;

    const [
        teamFilter,
        setTeamFilter,
    ] = useState("all");

    const [
        employeeFilter,
        setEmployeeFilter,
    ] = useState("all");

    const [
        search,
        setSearch,
    ] = useState("");
    const [isEmployeeSummaryOpen, setIsEmployeeSummaryOpen] = useState(false);
    const [isAbsentListOpen, setIsAbsentListOpen] = useState(false);
    const [attendanceTableWidth, setAttendanceTableWidth] = useState(1180);

    // ================= EDIT RECORD STATE & HANDLERS =================
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isManualModalOpen, setIsManualModalOpen] = useState(false);
    const [proofModalData, setProofModalData] = useState(null);
    const [editingRecord, setEditingRecord] = useState(null);
    const [escalationSendingId, setEscalationSendingId] = useState("");
    const [revertingAction, setRevertingAction] = useState("");
    const [manualSaving, setManualSaving] = useState(false);
    const emptyManualForm = {
        employeeIds: [],
        fromDate: attendanceRange.from,
        toDate: attendanceRange.to,
        status: "present",
        mode: "office",
        checkIn: "",
        checkOut: "",
        breakStart: "",
        breakEnd: "",
    };
    const [manualForm, setManualForm] = useState(emptyManualForm);
    const [editForm, setEditForm] = useState({
        date: "",
        status: "present",
        mode: "office",
        checkIn: "",
        checkOut: "",
        breakStart: "",
        breakEnd: "",
    });

    const combineDateAndTime = (dateStr, timeStr) => {
        if (!timeStr) return null;
        const trimmed = timeStr.trim().toUpperCase();
        const match = trimmed.match(/^(0?[1-9]|1[0-2]):([0-5][0-9])\s*(AM|PM)$/);
        if (!match) {
            const match24 = trimmed.match(/^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/);
            if (match24) {
                const [, hours, minutes] = match24;
                return createIndiaDateTimeIso(dateStr, Number(hours), Number(minutes), 0, 0);
            }
            throw new Error(`Invalid time format: "${timeStr}". Use HH:MM AM/PM format (e.g., 09:30 AM).`);
        }

        let [, hours, minutes, ampm] = match;
        let h = Number(hours);
        const m = Number(minutes);
        if (ampm === "PM" && h < 12) {
            h += 12;
        } else if (ampm === "AM" && h === 12) {
            h = 0;
        }

        return createIndiaDateTimeIso(dateStr, h, m, 0, 0);
    };

    const handleEditClick = (record) => {
        setEditingRecord(record);
        setEditForm({
            date: record.date || "",
            status: record.status || "present",
            mode: record.mode || "office",
            // CustomTimePicker accepts a 12-hour value. Formatting through the
            // attendance helper also keeps legacy timestamps correct.
            checkIn: formatAttendanceRecordTime(record, "checkIn", ""),
            checkOut: formatAttendanceRecordTime(record, "checkOut", ""),
            breakStart: formatAttendanceRecordTime(record, "breakStart", ""),
            breakEnd: formatAttendanceRecordTime(record, "breakEnd", ""),
        });
        setIsEditModalOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const body = {
                date: editForm.date,
                status: editForm.status,
                mode: editForm.mode,
                checkIn: combineDateAndTime(editForm.date, editForm.checkIn),
                checkOut: combineDateAndTime(editForm.date, editForm.checkOut),
                breakStart: combineDateAndTime(editForm.date, editForm.breakStart),
                breakEnd: combineDateAndTime(editForm.date, editForm.breakEnd),
            };

            const res = await api.patch(`/api/admin/attendance/${editingRecord._id}`, body);
            
            setRecords(prev => prev.map(r => r._id === editingRecord._id ? res.data : r));
            toast.success("Attendance record updated successfully!");
            setIsEditModalOpen(false);
            setEditingRecord(null);
        } catch (err) {
            console.error("Failed to update attendance:", err);
            toast.error(err.response?.data?.message || "Failed to update record. Please try again.");
        }
    };

    const openManualModal = () => {
        setManualForm({
            ...emptyManualForm,
            fromDate: attendanceRange.from,
            toDate: attendanceRange.to,
        });
        setIsManualModalOpen(true);
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault();
        if (!manualForm.employeeIds.length) {
            toast.error("Select at least one employee.");
            return;
        }
        if (!manualForm.fromDate || !manualForm.toDate) {
            toast.error("Select from date and to date.");
            return;
        }
        if (manualForm.toDate < manualForm.fromDate) {
            toast.error("To date cannot be before from date.");
            return;
        }
        const checkInMinutes = getManualTimeMinutes(manualForm.checkIn);
        const checkOutMinutes = getManualTimeMinutes(manualForm.checkOut);
        const lunchStartMinutes = getManualTimeMinutes(manualForm.breakStart);
        const lunchEndMinutes = getManualTimeMinutes(manualForm.breakEnd);
        if (checkInMinutes !== null && checkOutMinutes !== null && checkOutMinutes <= checkInMinutes) {
            toast.error("Check-out time must be after check-in time for each attendance day.");
            return;
        }
        if ((lunchStartMinutes === null) !== (lunchEndMinutes === null)) {
            toast.error("Enter both lunch start and lunch end times.");
            return;
        }
        if (lunchStartMinutes !== null && lunchEndMinutes !== null) {
            if (lunchEndMinutes <= lunchStartMinutes) {
                toast.error("Lunch end time must be after lunch start time.");
                return;
            }
            if (checkInMinutes !== null && checkOutMinutes !== null && (lunchStartMinutes < checkInMinutes || lunchEndMinutes > checkOutMinutes)) {
                toast.error("Lunch times must fall between check-in and check-out times.");
                return;
            }
        }
        setManualSaving(true);
        try {
            const body = {
                employeeIds: manualForm.employeeIds,
                fromDate: manualForm.fromDate,
                toDate: manualForm.toDate,
                status: manualForm.status,
                mode: manualForm.mode,
                checkInTime: manualForm.checkIn,
                checkOutTime: manualForm.checkOut,
                breakStartTime: manualForm.breakStart,
                breakEndTime: manualForm.breakEnd,
            };

            const res = await api.post("/api/admin/attendance/manual", body);
            const createdRecords = Array.isArray(res.data?.records) ? res.data.records : [];
            setRecords(prev => [
                ...createdRecords,
                ...prev.filter(item => !createdRecords.some(record => record._id === item._id)),
            ]);
            setSelectedFromDate(manualForm.fromDate);
            setSelectedToDate(manualForm.toDate || manualForm.fromDate);
            toast.success(res.data?.message || "Manual attendance added successfully.");
            setIsManualModalOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to add manual attendance.");
        } finally {
            setManualSaving(false);
        }
    };

    const getElapsedMinutes = (startTime, endTime = new Date()) => {
        const start = new Date(startTime).getTime();
        const end = new Date(endTime).getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
        return Math.floor((end - start) / 60000);
    };

    const hasLunchEscalationIssue = (record) => {
        const isActiveLunch = record?.breakStart && !record?.breakEnd && !record?.checkOut;
        if (isActiveLunch) {
            return getElapsedMinutes(record.breakStart) > LUNCH_LIMIT_MINUTES;
        }
        return getLunchDurationMinutes(record) > LUNCH_LIMIT_MINUTES;
    };

    const shouldShowEscalationMail = (record) => {
        const isTodayRecord = getAttendanceDateKey(record?.date) === formatISO(new Date());
        return isTodayRecord && (!record?.checkIn || !record?.checkOut || hasLunchEscalationIssue(record));
    };

    const handleSendEscalationMail = async (record) => {
        if (!record?._id) return;
        setEscalationSendingId(record._id);
        try {
            const res = await api.post(`/api/admin/attendance/${record._id}/escalation-mail`);
            toast.success(res.data?.message || "Escalation mail sent.");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to send escalation mail.");
        } finally {
            setEscalationSendingId("");
        }
    };

    const attendanceActionLabels = {
        checkIn: "Check In",
        checkOut: "Check Out",
        breakStart: "Lunch Start",
        breakEnd: "Lunch End",
    };

    const handleRevertAttendanceAction = async (record, field) => {
        if (!record?._id || !field) return;
        const label = attendanceActionLabels[field] || "Attendance action";
        toast((toastItem) => (
            <div className="flex max-w-sm items-center gap-3">
                <span className="text-sm font-semibold">
                    Revert {label} for {record.employee?.name || "this employee"}?
                </span>
                <button
                    type="button"
                    className="rounded-lg bg-red-500 px-3 py-1 text-xs font-black text-white"
                    onClick={async () => {
                        toast.dismiss(toastItem.id);
                        const busyKey = `${record._id}-${field}`;
                        setRevertingAction(busyKey);
                        try {
                            const res = await api.patch(`/api/admin/attendance/${record._id}/revert-action`, { field });
                            const updated = res.data?.attendance;
                            if (updated?._id) {
                                setRecords(prev => prev.map(item => item._id === updated._id ? updated : item));
                            }
                            toast.success(res.data?.message || `${label} reverted.`);
                        } catch (err) {
                            toast.error(err.response?.data?.message || `Failed to revert ${label}.`);
                        } finally {
                            setRevertingAction("");
                        }
                    }}
                >
                    Revert
                </button>
            </div>
        ));
    };

    // ================= FORMAT =================
    const formatRecordTime = (record, field) =>
        formatAttendanceRecordTime(record, field, "-");

    const formatDate =
        (d) => {

            if (!d)
                return "—";

            const [year, month, day] = String(d).slice(0, 10).split("-");
            if (year && month && day) return `${day}-${month}-${year}`;

            return new Date(d).toLocaleDateString("en-IN");
        };

    const attendanceRangeLabel = attendanceRange.from === attendanceRange.to
        ? formatDate(attendanceRange.from)
        : `${formatDate(attendanceRange.from)} to ${formatDate(attendanceRange.to)}`;

    const formatLunchDuration =
        (mins) => {

            if (!mins)
                return "—";

            const totalMinutes = Math.round(Number(mins || 0));

            if (totalMinutes < 60) {
                return `${totalMinutes} mins`;
            }

            const hours =
                Math.floor(
                    totalMinutes / 60,
                );

            const remaining =
                totalMinutes % 60;

            return `${hours}h ${remaining}m`;
        };

    const getLunchDurationMinutes = (record = {}) => {
        const storedMinutes = Number(record?.breakDuration || 0);
        if (storedMinutes > 0) return storedMinutes;
        if (!record?.breakStart || !record?.breakEnd) return 0;
        return getElapsedMinutes(record.breakStart, record.breakEnd);
    };

    const renderLunchDuration = (record = {}) => {
        const minutes = getLunchDurationMinutes(record);
        return minutes > 0 ? formatLunchDuration(minutes) : "-";
    };

    const getAttendanceWorkHours = (record) => {
        return getWorkDurationLabel(record);
    };

    const AutoCheckoutDisplay = ({ time }) => (
        <span className="inline-flex min-w-[7.25rem] flex-col items-start gap-1 leading-tight">
            <span className="whitespace-nowrap text-xs font-bold text-[var(--text-primary)]">{time}</span>
            <span className="whitespace-nowrap rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[8px] font-black uppercase leading-none text-amber-700 ring-1 ring-amber-500/20 dark:text-amber-300">
                Auto checkout
            </span>
        </span>
    );

    const formatAttendanceCheckoutText = (record) => (
        isAutoCheckoutRecord(record) ? `${getAutoCheckoutDisplayTime(record, () => formatRecordTime(record, "checkOut"))} (Auto checkout)` : formatRecordTime(record, "checkOut")
    );

    const renderAttendanceCheckout = (record) => (
        isAutoCheckoutRecord(record) ? <AutoCheckoutDisplay time={getAutoCheckoutDisplayTime(record, () => formatRecordTime(record, "checkOut"))} /> : formatRecordTime(record, "checkOut")
    );

    const renderAttendanceHours = (record) => getAttendanceWorkHours(record);

    // ================= FETCH =================
    const handleAuthError = useCallback((err) => {
        if (
            err?.response?.status === 401 ||
            err?.response?.status === 403
        ) {
            navigate(
                "/admin/login",
            );
        }
    }, [navigate]);

    const fetchEmployees = useCallback(async (includeEscalations = false) => {
        try {
            const res = await api.get(
                "/api/admin/employees/all",
                {
                    params: {
                        includeEscalations,
                    },
                },
            );

            const employeeList = Array.isArray(res.data) ? res.data : [];
            setEmployees(employeeList);
        } catch (err) {
            console.error(err);
            handleAuthError(err);
        } finally {
            setEmployeesLoading(false);
        }
    }, [handleAuthError]);

    const fetchRecords = useCallback(
        async (fromDate, toDate) => {
            setAttendanceLoading(true);

            try {

                const attendanceRes =
                    await api.get(
                        "/api/admin/attendance",
                        {
                            params: {
                                startDate: fromDate,
                                endDate: toDate,
                                includeAbsenceSummary: false,
                                includeEscalations: false,
                            },
                        },
                    );

                const responseRecords = Array.isArray(attendanceRes.data)
                    ? attendanceRes.data
                    : attendanceRes.data?.records;
                setRecords(
                    Array.isArray(responseRecords) ? responseRecords : [],
                );

            } catch (err) {

                console.error(err);
                handleAuthError(err);
            } finally {

                setAttendanceLoading(false);
            }
        },
        [handleAuthError],
    );

    const fetchAttendanceDetails = useCallback(async (fromDate, toDate) => {
        try {
            const res = await api.get("/api/admin/attendance", {
                params: {
                    startDate: fromDate,
                    endDate: toDate,
                    includeAbsenceSummary: true,
                    includeEscalations: true,
                },
            });
            const detailedRecords = Array.isArray(res.data?.records) ? res.data.records : [];
            const absentDays = Array.isArray(res.data?.absentEmployeeDays) ? res.data.absentEmployeeDays : [];
            const approvedLeaveDays = Array.isArray(res.data?.approvedLeaveEmployeeDays)
                ? res.data.approvedLeaveEmployeeDays
                : [];
            if (detailedRecords.length) setRecords(detailedRecords);
            setServerAbsentEmployeeDays(absentDays);
            setServerApprovedLeaveEmployeeDays(approvedLeaveDays);
        } catch (err) {
            console.error("Failed to enrich attendance report:", err);
            handleAuthError(err);
        }
    }, [handleAuthError]);

    useEffect(() => {
        document.title = "Attendance Report | Savrion Technologies";
        fetchEmployees(false);
        const timer = window.setTimeout(() => fetchEmployees(true), 800);
        return () => window.clearTimeout(timer);
    }, [fetchEmployees]);

    useEffect(() => {
        document.title = "Attendance Report | Savrion Technologies";
        let active = true;
        let detailTimer;
        fetchRecords(attendanceRange.from, attendanceRange.to).finally(() => {
            if (!active) return;
            detailTimer = window.setTimeout(() => {
                fetchAttendanceDetails(attendanceRange.from, attendanceRange.to);
            }, 250);
        });
        return () => {
            active = false;
            if (detailTimer) window.clearTimeout(detailTimer);
        };
    }, [fetchAttendanceDetails, fetchRecords, attendanceRange.from, attendanceRange.to]);

    // ================= UNIQUE =================
    const uniqueTeams =
        useMemo(() => {

            return [
                ...new Set(
                    [
                        ...records.map((r) => r.employee?.team),
                        ...employees.map((employee) => employee.team),
                    ].filter(
                        Boolean,
                    ),
                ),
            ];

        }, [records, employees]);

    const uniqueEmployees =
        useMemo(() => {

            const map =
                new Map();

            records.forEach(
                (r) => {

                    if (
                        r.employee
                            ?._id
                    ) {
                        map.set(
                            r
                                .employee
                                ._id,
                            r
                                .employee,
                        );
                    }
                },
            );

            return Array.from(
                map.values(),
            );

        }, [records]);

    const manualEmployeeOptions =
        useMemo(() => {
            const map = new Map();
            [...employees, ...uniqueEmployees].forEach((employee) => {
                if (!employee?._id) return;
                map.set(employee._id, {
                    ...(map.get(employee._id) || {}),
                    ...employee,
                    employeeId: employee.employeeId || map.get(employee._id)?.employeeId || "",
                });
            });
            return Array.from(map.values()).sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
        }, [employees, uniqueEmployees]);

    // ================= FILTER =================
    const filteredRecords =
        useMemo(() => {

            return records.filter(
                (r) => {

                    const recordDate = getAttendanceDateKey(r.date);
                    const dateMatch =
                        recordDate >= attendanceRange.from &&
                        recordDate <= attendanceRange.to;

                    const teamMatch =
                        teamFilter ===
                        "all" ||
                        r.employee
                            ?.team ===
                        teamFilter;

                    const employeeMatch =
                        employeeFilter ===
                        "all" ||
                        r.employee
                            ?._id ===
                        employeeFilter;

                    const searchMatch =
                        r.employee?.name
                            ?.toLowerCase()
                            .includes(
                                search.toLowerCase(),
                            ) ||
                        r.employee?.employeeId
                            ?.toLowerCase()
                            .includes(
                                search.toLowerCase(),
                            );

                    return (
                        dateMatch &&
                        teamMatch &&
                        employeeMatch &&
                        searchMatch
                    );
                },
            );

        }, [
            records,
            attendanceRange.from,
            attendanceRange.to,
            teamFilter,
            employeeFilter,
            search,
        ]);

    const absentEmployeeDays =
        useMemo(() => {
            const query = search.trim().toLowerCase();
            const recordedEmployeeDateKeys = new Set(
                filteredRecords.map((record) => `${record.employee?._id || ""}|${getAttendanceDateKey(record.date)}`),
            );

            return serverAbsentEmployeeDays.filter(({ employee, date }) => {
                const recordDate = getAttendanceDateKey(date);
                const teamMatch =
                    teamFilter === "all" ||
                    employee?.team === teamFilter;
                const employeeMatch =
                    employeeFilter === "all" ||
                    employee?._id === employeeFilter;
                const searchMatch =
                    !query ||
                    String(employee?.name || "").toLowerCase().includes(query) ||
                    String(employee?.employeeId || "").toLowerCase().includes(query);

                return (
                    recordDate >= attendanceRange.from &&
                    recordDate <= attendanceRange.to &&
                    !recordedEmployeeDateKeys.has(`${employee?._id || ""}|${recordDate}`) &&
                    teamMatch &&
                    employeeMatch &&
                    searchMatch
                );
            });
        }, [serverAbsentEmployeeDays, filteredRecords, attendanceRange.from, attendanceRange.to, teamFilter, employeeFilter, search]);

    const approvedLeaveEmployeeDays = useMemo(() => {
        const query = search.trim().toLowerCase();
        const recordedEmployeeDateKeys = new Set(
            filteredRecords.map((record) => `${record.employee?._id || ""}|${getAttendanceDateKey(record.date)}`),
        );

        return serverApprovedLeaveEmployeeDays.filter(({ employee, date }) => {
            const recordDate = getAttendanceDateKey(date);
            const teamMatch = teamFilter === "all" || employee?.team === teamFilter;
            const employeeMatch = employeeFilter === "all" || employee?._id === employeeFilter;
            const searchMatch =
                !query ||
                String(employee?.name || "").toLowerCase().includes(query) ||
                String(employee?.employeeId || "").toLowerCase().includes(query);

            return (
                recordDate >= attendanceRange.from &&
                recordDate <= attendanceRange.to &&
                !recordedEmployeeDateKeys.has(`${employee?._id || ""}|${recordDate}`) &&
                teamMatch &&
                employeeMatch &&
                searchMatch
            );
        });
    }, [
        serverApprovedLeaveEmployeeDays,
        filteredRecords,
        attendanceRange.from,
        attendanceRange.to,
        teamFilter,
        employeeFilter,
        search,
    ]);

    const absentEmployees = useMemo(() => {
        const map = new Map();
        absentEmployeeDays.forEach(({ employee }) => {
            if (employee?._id) map.set(employee._id, employee);
        });
        return Array.from(map.values());
    }, [absentEmployeeDays]);

    const absenceRegisterEmployees = useMemo(() => {
        const map = new Map();
        [...absentEmployeeDays, ...approvedLeaveEmployeeDays].forEach(({ employee }) => {
            if (employee?._id) map.set(employee._id, employee);
        });
        return Array.from(map.values()).sort((a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || "")),
        );
    }, [absentEmployeeDays, approvedLeaveEmployeeDays]);

    const presentEmployees = useMemo(() => {
        const map = new Map();
        filteredRecords.forEach((record) => {
            if (record?.checkIn && record.employee?._id) {
                map.set(record.employee._id, record.employee);
            }
        });
        return Array.from(map.values());
    }, [filteredRecords]);

    const tableRecords =
        useMemo(() => {
            const absentRows = absentEmployeeDays.map(({ employee, date }) => ({
                _id: `absent-${employee._id}-${date}`,
                employee,
                date,
                mode: "",
                status: "absent",
                isAbsentPlaceholder: true,
            }));
            const leaveRows = approvedLeaveEmployeeDays.map(({ employee, date, leaveType }) => ({
                _id: `leave-${employee._id}-${date}`,
                employee,
                date,
                mode: "",
                status: "leave",
                leaveType,
                isApprovedLeavePlaceholder: true,
            }));

            return [...filteredRecords, ...absentRows, ...leaveRows];
        }, [filteredRecords, absentEmployeeDays, approvedLeaveEmployeeDays]);

    const tableDateKeys = useMemo(() => (
        Array.from(new Set(
            tableRecords
                .map((record) => getAttendanceDateKey(record.date))
                .filter(Boolean),
        )).sort((a, b) => a.localeCompare(b))
    ), [tableRecords]);

    const [selectedTableDate, setSelectedTableDate] = useState("");

    useEffect(() => {
        document.title = "Attendance Report | Savrion Technologies";
        setSelectedTableDate((current) => (
            current && tableDateKeys.includes(current)
                ? current
                : tableDateKeys[0] || ""
        ));
    }, [tableDateKeys]);

    const activeTableDate = tableDateKeys.includes(selectedTableDate)
        ? selectedTableDate
        : tableDateKeys[0] || "";
    const activeTableDateIndex = Math.max(0, tableDateKeys.indexOf(activeTableDate));
    const pagedTableRecords = useMemo(() => (
        activeTableDate
            ? tableRecords.filter((record) => getAttendanceDateKey(record.date) === activeTableDate)
            : []
    ), [tableRecords, activeTableDate]);

    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        document.title = "Attendance Report | Savrion Technologies";
        setCurrentPage(1);
    }, [activeTableDate, tableRecords]);

    const displayedTableRecords = useMemo(() => {
        if (pageSize === 'all') return pagedTableRecords;
        const start = (currentPage - 1) * Number(pageSize);
        return pagedTableRecords.slice(start, start + Number(pageSize));
    }, [pagedTableRecords, currentPage, pageSize]);

    const totalPages = useMemo(() => {
        if (pageSize === 'all' || !pagedTableRecords.length) return 1;
        return Math.ceil(pagedTableRecords.length / Number(pageSize));
    }, [pagedTableRecords.length, pageSize]);

    useEffect(() => {
        document.title = "Attendance Report | Savrion Technologies";
        const updateTableWidth = () => {
            if (attendanceTableRef.current) {
                setAttendanceTableWidth(attendanceTableRef.current.scrollWidth);
            }
        };
        updateTableWidth();
        window.addEventListener("resize", updateTableWidth);
        return () => window.removeEventListener("resize", updateTableWidth);
    }, [pagedTableRecords]);

    const dailyAttendanceSummaries = useMemo(() => {
        const summaries = new Map();

        tableRecords.forEach((record) => {
            const dateKey = getAttendanceDateKey(record.date);
            const employee = record.employee;
            const employeeKey = employee?._id || employee?.employeeId || employee?.name;
            if (!dateKey || !employeeKey) return;

            if (!summaries.has(dateKey)) {
                summaries.set(dateKey, {
                    date: dateKey,
                    presentEmployees: new Map(),
                    absentEmployees: new Map(),
                    leaveEmployees: new Map(),
                });
            }

            const dateSummary = summaries.get(dateKey);
            const isAbsent = record.isAbsentPlaceholder || String(record.status || "").toLowerCase() === "absent";
            const isOnApprovedLeave = record.isApprovedLeavePlaceholder;

            if (isOnApprovedLeave) {
                dateSummary.leaveEmployees.set(employeeKey, {
                    ...employee,
                    leaveType: record.leaveType,
                });
                dateSummary.presentEmployees.delete(employeeKey);
                dateSummary.absentEmployees.delete(employeeKey);
            } else if (isAbsent && !dateSummary.leaveEmployees.has(employeeKey)) {
                dateSummary.absentEmployees.set(employeeKey, employee);
                dateSummary.presentEmployees.delete(employeeKey);
            } else if (
                !dateSummary.absentEmployees.has(employeeKey) &&
                !dateSummary.leaveEmployees.has(employeeKey)
            ) {
                dateSummary.presentEmployees.set(employeeKey, employee);
            }
        });

        const sortEmployees = (employeeMap) => Array.from(employeeMap.values()).sort((a, b) =>
            String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" }),
        );

        return Array.from(summaries.values())
            .map((dateSummary) => ({
                date: dateSummary.date,
                presentEmployees: sortEmployees(dateSummary.presentEmployees),
                absentEmployees: sortEmployees(dateSummary.absentEmployees),
                leaveEmployees: sortEmployees(dateSummary.leaveEmployees),
            }))
            .sort((a, b) => b.date.localeCompare(a.date));
    }, [tableRecords]);

    // ================= SUMMARY =================
    const summary =
        useMemo(() => {

            const total =
                filteredRecords.length;

            const present =
                presentEmployees.length;

            const wfh =
                filteredRecords.filter(
                    (r) =>
                        r.mode ===
                        "wfh",
                ).length;

            const onBreak =
                filteredRecords.filter(
                    (r) =>
                        r.breakStart &&
                        !r.breakEnd &&
                        !r.checkOut,
                ).length;

            return {
                total,
                present,
                wfh,
                onBreak,
                absent: absentEmployees.length,
            };

        }, [
            filteredRecords,
            absentEmployees,
            presentEmployees,
        ]);

    // ================= EXPORT EXCEL =================
    const setWorksheetLayout = (worksheet, columnWidths) => {
        worksheet["!cols"] = columnWidths.map((wch) => ({ wch }));

        if (worksheet["!ref"]) {
            const range = XLSX.utils.decode_range(worksheet["!ref"]);
            worksheet["!autofilter"] = {
                ref: XLSX.utils.encode_range({
                    s: { r: range.s.r, c: range.s.c },
                    e: { r: range.s.r, c: range.e.c },
                }),
            };
        }
    };

    const exportDailyAttendanceSummary = () => {
        if (!dailyAttendanceSummaries.length) {
            toast.error("No daily attendance summary is available to export.");
            return;
        }

        const detailRows = [];
        const totalRows = [];

        [...dailyAttendanceSummaries]
            .sort((a, b) => a.date.localeCompare(b.date))
            .forEach(({ date, presentEmployees: dayPresent, absentEmployees: dayAbsent, leaveEmployees: dayOnLeave }) => {
                const addEmployeeRows = (employees, status) => {
                    employees.forEach((employee) => {
                        detailRows.push({
                            Date: formatDate(date),
                            "Employee Name": employee?.name || "Unknown employee",
                            "Employee ID": employee?.employeeId || "-",
                            Team: formatTeamName(employee?.team),
                            Status: status,
                            "Leave Type": status === "On Leave"
                                ? (employee?.leaveType === "sick" ? "Medical" : employee?.leaveType || "Approved")
                                : "-",
                        });
                    });
                };

                addEmployeeRows(dayPresent, "Present");
                addEmployeeRows(dayAbsent, "Absent");
                addEmployeeRows(dayOnLeave, "On Leave");

                totalRows.push({
                    Date: formatDate(date),
                    Present: dayPresent.length,
                    Absent: dayAbsent.length,
                    "On Leave": dayOnLeave.length,
                    "Total Employees": dayPresent.length + dayAbsent.length + dayOnLeave.length,
                });
            });

        const detailsSheet = XLSX.utils.json_to_sheet(detailRows);
        const totalsSheet = XLSX.utils.json_to_sheet(totalRows);
        setWorksheetLayout(detailsSheet, [16, 28, 18, 22, 16, 18]);
        setWorksheetLayout(totalsSheet, [16, 14, 14, 14, 20]);

        const headerStyle = {
            fill: { patternType: "solid", fgColor: { rgb: "E5E7EB" } },
            font: { bold: true, color: { rgb: "111827" } },
            alignment: { vertical: "center" },
        };
        const absentStyle = {
            fill: { patternType: "solid", fgColor: { rgb: "FEE2E2" } },
            font: { bold: true, color: { rgb: "DC2626" } },
        };

        for (let column = 0; column < 6; column += 1) {
            const cell = detailsSheet[XLSX.utils.encode_cell({ r: 0, c: column })];
            if (cell) cell.s = headerStyle;
        }
        detailRows.forEach((row, rowIndex) => {
            if (row.Status !== "Absent") return;
            for (let column = 0; column < 6; column += 1) {
                const cell = detailsSheet[XLSX.utils.encode_cell({ r: rowIndex + 1, c: column })];
                if (cell) cell.s = absentStyle;
            }
        });

        for (let column = 0; column < 5; column += 1) {
            const cell = totalsSheet[XLSX.utils.encode_cell({ r: 0, c: column })];
            if (cell) cell.s = headerStyle;
        }
        totalRows.forEach((row, rowIndex) => {
            const absentCell = totalsSheet[XLSX.utils.encode_cell({ r: rowIndex + 1, c: 2 })];
            if (absentCell && row.Absent > 0) absentCell.s = absentStyle;
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, detailsSheet, "Employee Status");
        XLSX.utils.book_append_sheet(workbook, totalsSheet, "Daily Totals");

        const rangeLabel = selectedFromDate === selectedToDate
            ? selectedFromDate
            : `${selectedFromDate}_to_${selectedToDate}`;
        XLSX.writeFile(workbook, `daily_attendance_summary_${rangeLabel}.xlsx`);
        toast.success("Daily attendance summary exported.");
    };

    const exportExcel =
        (
            type = "all",
        ) => {

            let exportData =
                tableRecords;

            if (
                type ===
                "team"
            ) {

                if (
                    teamFilter ===
                    "all"
                ) {
                    toast.error("Select a team first.");

                    return;
                }

                exportData =
                    tableRecords.filter(
                        (
                            r,
                        ) =>
                            r
                                .employee
                                ?.team ===
                            teamFilter,
                    );
            }

            if (
                type ===
                "employee"
            ) {

                if (
                    employeeFilter ===
                    "all"
                ) {
                    toast.error("Select employee first.");

                    return;
                }

                exportData =
                    tableRecords.filter(
                        (
                            r,
                        ) =>
                            r
                                .employee
                                ?._id ===
                            employeeFilter,
                    );
            }

            const sortedExportData = [...exportData].sort((a, b) => {
                const dateComparison = getAttendanceDateKey(a.date).localeCompare(getAttendanceDateKey(b.date));
                if (dateComparison !== 0) return dateComparison;

                return String(a.employee?.name || "").localeCompare(String(b.employee?.name || ""));
            });
            const approvedLeaveKeys = new Set(
                sortedExportData
                    .filter((record) => record.isApprovedLeavePlaceholder)
                    .map((record) => `${record.employee?._id || record.employee?.employeeId}|${getAttendanceDateKey(record.date)}`),
            );
            const summaryExportData = sortedExportData.filter((record) => (
                record.isApprovedLeavePlaceholder ||
                !approvedLeaveKeys.has(`${record.employee?._id || record.employee?.employeeId}|${getAttendanceDateKey(record.date)}`)
            ));

            const excelData =
                summaryExportData.map(
                    (
                        r,
                    ) => ({

                        Employee:
                            r
                                .employee
                                ?.name,

                        EmployeeID:
                            r
                                .employee
                                ?.employeeId,

                        Team:
                            formatTeamName(
                                r
                                    .employee
                                    ?.team,
                            ),

                        Date:
                            r.displayDate || formatDate(r.date),

                        Mode:
                            r.mode || "—",

                        CheckIn:
                            formatRecordTime(r, "checkIn"),

                        LunchDuration:
                            renderLunchDuration(r),

                        LunchStart:
                            formatRecordTime(r, "breakStart"),

                        LunchEnd:
                            formatRecordTime(r, "breakEnd"),

                        CheckOut:
                            formatAttendanceCheckoutText(r),

                        WorkHours:
                            getAttendanceWorkHours(r),

                        Status:
                            r.isApprovedLeavePlaceholder
                                ? `On Leave (${r.leaveType === "sick" ? "Medical" : r.leaveType || "Approved"})`
                                : r.isAbsentPlaceholder
                                ? "Absent"
                                : r.checkOut
                                ? "Completed"
                                : r.breakStart &&
                                    !r.breakEnd
                                    ? "At Lunch"
                                    : r.checkIn
                                        ? "Working"
                                        : "Absent",
                    }),
                );

            const dailySummaryMap = new Map();
            const monthlySummaryMap = new Map();
            const employeeSummaryMap = new Map();

            summaryExportData.forEach((record) => {
                const dateKey = getAttendanceDateKey(record.date);
                if (!dateKey) return;

                const isAbsent = record.isAbsentPlaceholder || String(record.status || "").toLowerCase() === "absent";
                const isOnLeave = record.isApprovedLeavePlaceholder;
                const employeeId = String(record.employee?._id || record.employee?.employeeId || record.employee?.name || "");
                const monthKey = dateKey.slice(0, 7);

                if (!employeeSummaryMap.has(employeeId)) {
                    employeeSummaryMap.set(employeeId, {
                        employee: record.employee,
                        dates: new Set(),
                        presentDates: new Set(),
                        absentDates: new Set(),
                        leaveDates: new Set(),
                        monthlyAbsentDates: new Map(),
                    });
                }

                const employeeSummary = employeeSummaryMap.get(employeeId);
                employeeSummary.dates.add(dateKey);
                if (isOnLeave) {
                    employeeSummary.leaveDates.add(dateKey);
                } else if (isAbsent) {
                    employeeSummary.absentDates.add(dateKey);
                    if (!employeeSummary.monthlyAbsentDates.has(monthKey)) {
                        employeeSummary.monthlyAbsentDates.set(monthKey, new Set());
                    }
                    employeeSummary.monthlyAbsentDates.get(monthKey).add(dateKey);
                } else {
                    employeeSummary.presentDates.add(dateKey);
                }

                if (!dailySummaryMap.has(dateKey)) {
                    dailySummaryMap.set(dateKey, {
                        date: dateKey,
                        presentIds: new Set(),
                        absentIds: new Set(),
                        absentNames: new Set(),
                        leaveIds: new Set(),
                        leaveNames: new Set(),
                    });
                }

                const daily = dailySummaryMap.get(dateKey);
                if (isOnLeave) {
                    if (employeeId) daily.leaveIds.add(employeeId);
                    if (record.employee?.name) daily.leaveNames.add(record.employee.name);
                } else if (isAbsent) {
                    if (employeeId) daily.absentIds.add(employeeId);
                    if (record.employee?.name) daily.absentNames.add(record.employee.name);
                } else if (employeeId) {
                    daily.presentIds.add(employeeId);
                }

                if (!monthlySummaryMap.has(monthKey)) {
                    monthlySummaryMap.set(monthKey, {
                        month: monthKey,
                        dates: new Set(),
                        presentEmployeeDays: 0,
                        absentEmployeeDays: 0,
                        leaveEmployeeDays: 0,
                        absentIds: new Set(),
                    });
                }

                const monthly = monthlySummaryMap.get(monthKey);
                monthly.dates.add(dateKey);
                if (isOnLeave) {
                    monthly.leaveEmployeeDays += 1;
                } else if (isAbsent) {
                    monthly.absentEmployeeDays += 1;
                    if (employeeId) monthly.absentIds.add(employeeId);
                } else {
                    monthly.presentEmployeeDays += 1;
                }
            });

            const dailySummaryData = Array.from(dailySummaryMap.values())
                .sort((a, b) => a.date.localeCompare(b.date))
                .map((day) => ({
                    Date: formatDate(day.date),
                    Present: day.presentIds.size,
                    Absent: day.absentIds.size,
                    "On Leave": day.leaveIds.size,
                    "Total Employees": day.presentIds.size + day.absentIds.size + day.leaveIds.size,
                    "Absent Employee Names": Array.from(day.absentNames).sort().join(", ") || "-",
                    "On Leave Employee Names": Array.from(day.leaveNames).sort().join(", ") || "-",
                }));

            const monthlySummaryData = Array.from(monthlySummaryMap.values())
                .sort((a, b) => a.month.localeCompare(b.month))
                .map((month) => ({
                    Month: month.month,
                    "Days Included": month.dates.size,
                    "Present Employee-Days": month.presentEmployeeDays,
                    "Absent Employee-Days": month.absentEmployeeDays,
                    "Approved Leave Employee-Days": month.leaveEmployeeDays,
                    "Employees Absent At Least Once": month.absentIds.size,
                }));

            const employeeSummaryData = Array.from(employeeSummaryMap.values())
                .sort((a, b) => String(a.employee?.name || "").localeCompare(String(b.employee?.name || "")))
                .map((summaryRecord) => ({
                    "Employee Name": summaryRecord.employee?.name || "-",
                    "Employee ID": summaryRecord.employee?.employeeId || "-",
                    Team: formatTeamName(summaryRecord.employee?.team),
                    "Total Working Days": summaryRecord.dates.size,
                    "Present Days": summaryRecord.presentDates.size,
                    "Absent Days": summaryRecord.absentDates.size,
                    "Approved Leave Days": summaryRecord.leaveDates.size,
                    "Present Dates": Array.from(summaryRecord.presentDates)
                        .sort()
                        .map((date) => formatDate(date))
                        .join(", ") || "-",
                    "Absent Dates": Array.from(summaryRecord.absentDates)
                        .sort()
                        .map((date) => formatDate(date))
                        .join(", ") || "-",
                    "Approved Leave Dates": Array.from(summaryRecord.leaveDates)
                        .sort()
                        .map((date) => formatDate(date))
                        .join(", ") || "-",
                    "Monthly Absence": Array.from(summaryRecord.monthlyAbsentDates.entries())
                        .sort(([a], [b]) => a.localeCompare(b))
                        .map(([month, dates]) => `${month}: ${dates.size}`)
                        .join(", ") || "-",
                }));

            const worksheet =
                XLSX.utils.json_to_sheet(
                    excelData,
                );
            const employeeSummaryWorksheet = XLSX.utils.json_to_sheet(employeeSummaryData);
            const dailySummaryWorksheet = XLSX.utils.json_to_sheet(dailySummaryData);
            const monthlySummaryWorksheet = XLSX.utils.json_to_sheet(monthlySummaryData);

            setWorksheetLayout(worksheet, [24, 16, 20, 14, 12, 14, 18, 14, 14, 18, 16, 14]);
            setWorksheetLayout(employeeSummaryWorksheet, [24, 16, 20, 20, 16, 16, 20, 72, 60, 60, 36]);
            setWorksheetLayout(dailySummaryWorksheet, [14, 12, 12, 14, 18, 60, 60]);
            setWorksheetLayout(monthlySummaryWorksheet, [14, 14, 24, 24, 28, 32]);

            const workbook =
                XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(
                workbook,
                worksheet,
                "Attendance Details",
            );
            XLSX.utils.book_append_sheet(
                workbook,
                employeeSummaryWorksheet,
                "Employee Summary",
            );
            XLSX.utils.book_append_sheet(
                workbook,
                dailySummaryWorksheet,
                "Daily Summary",
            );
            XLSX.utils.book_append_sheet(
                workbook,
                monthlySummaryWorksheet,
                "Monthly Summary",
            );

            let fileName =
                `attendance_${attendanceRangeFileKey}.xlsx`;

            if (
                type ===
                "team"
            ) {
                fileName = `${formatTeamName(teamFilter)}_attendance_${attendanceRangeFileKey}.xlsx`;
            }

            if (
                type ===
                "employee"
            ) {

                const emp =
                    uniqueEmployees.find(
                        (
                            e,
                        ) =>
                            e._id ===
                            employeeFilter,
                    );

                fileName = `${emp?.name || "employee"
                    }_attendance_${attendanceRangeFileKey}.xlsx`;
            }

            XLSX.writeFile(
                workbook,
                fileName,
            );
        };

    return (
        <div
            className="
                flex flex-col xl:flex-row
                min-h-screen
                bg-[var(--bg-primary)]
                text-[var(--text-primary)]
            "
        >

            <AdminNavbar />

            <div
                className="
                    flex-1
                    min-w-0
                    w-full
                    max-w-full
                    px-4
                    md:px-6
                    py-4
                    space-y-4
                "
            >

                {/* HERO */}
                <div
                    className="
                        px-1
                        py-1
                    "
                >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-xl font-black tracking-tight md:text-2xl">Attendance Report</h1>
                            <p className="mt-1 text-xs text-[var(--text-secondary)] md:text-sm">
                                Showing attendance for {attendanceRangeLabel}.
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={openManualModal}
                                className="
                                    inline-flex
                                    items-center
                                    gap-2
                                    rounded-lg
                                    bg-[var(--accent-primary)]
                                    px-4
                                    py-2.5
                                    text-sm
                                    font-bold
                                    text-white
                                    transition-all
                                    hover:bg-[var(--accent-hover)]
                                "
                            >
                                <Plus size={18} />
                                Manual Entry
                            </button>
                        </div>
                    </div>
                </div>

                {/* STATS */}
                <div className="grid grid-cols-2 overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] lg:grid-cols-5">
                    {[
                        { title: "Total records", value: summary.total, icon: <Users size={18} />, tone: "text-slate-600 bg-[var(--bg-secondary)]0/10" },
                        { title: "Present", value: summary.present, icon: <CheckCircle2 size={18} />, tone: "text-emerald-600 bg-emerald-500/10" },
                        { title: "Absent", value: summary.absent, icon: <X size={18} />, tone: "text-red-500 bg-red-500/10", opensAbsentList: true },
                        { title: "Working remotely", value: summary.wfh, icon: <Building2 size={18} />, tone: "text-[var(--accent-primary)] bg-[var(--accent-primary)]/10" },
                        { title: "On lunch", value: summary.onBreak, icon: <Coffee size={18} />, tone: "text-amber-600 bg-amber-500/10" },
                    ].map((item, index) => (
                        <div
                            key={index}
                            onClick={item.opensAbsentList ? () => setIsAbsentListOpen(true) : undefined}
                            onKeyDown={item.opensAbsentList ? (event) => {
                                if (event.key === "Enter" || event.key === " ") setIsAbsentListOpen(true);
                            } : undefined}
                            role={item.opensAbsentList ? "button" : undefined}
                            tabIndex={item.opensAbsentList ? 0 : undefined}
                            className={`flex min-w-0 items-center gap-3 border-b border-r border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-4 transition last:border-r-0 lg:border-b-0 ${
                                item.opensAbsentList
                                    ? "cursor-pointer hover:bg-red-500/[0.025] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500/20"
                                    : ""
                            }`}
                        >
                            <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${item.tone}`}>
                                {item.icon}
                            </div>
                            <div className="min-w-0">
                                <h2 className="text-xl font-black leading-none">{item.value}</h2>
                                <p className="mt-1 truncate text-[11px] font-semibold text-[var(--text-secondary)]">{item.title}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* FILTERS */}
                <div
                    className="
                        rounded-xl
                        border
                        border-[var(--border-color)]
                        bg-[var(--card-bg)]
                        p-4
                        space-y-3
                    "
                >
                    <div className="flex flex-col gap-1 border-b border-[var(--border-color)] pb-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="text-sm font-black">Attendance filters</h2>
                            <p className="text-[11px] text-[var(--text-secondary)]">Search employees or select a reporting period.</p>
                        </div>
                        <span className="w-fit rounded-md bg-[var(--bg-primary)] px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-[var(--text-secondary)]">
                            {attendanceRangeLabel}
                        </span>
                    </div>

                    <div
                        className="
                            grid
                            grid-cols-1
                            md:grid-cols-2
                            lg:grid-cols-6
                            gap-3
                        "
                    >

                        {/* SEARCH */}
                        <div className="relative lg:col-span-2">

                            <Search
                                size={18}
                                className="
                                    absolute
                                    left-4
                                    top-1/2
                                    -translate-y-1/2
                                    text-[var(--text-secondary)]
                                "
                            />

                            <input
                                type="text"
                                placeholder="Search employee..."
                                value={
                                    search
                                }
                                onChange={(
                                    e,
                                ) =>
                                    setSearch(
                                        e
                                            .target
                                            .value,
                                    )
                                }
                                className="
                                    w-full
                                    pl-11
                                    pr-4
                                    py-2.5
                                    rounded-xl
                                    border
                                    border-[var(--border-color)]
                                    bg-[var(--bg-primary)]
                                    outline-none
                                "
                            />

                        </div>

                        {/* FROM DATE */}
                        <label className="flex items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2.5">
                            <span className="text-xs font-black uppercase text-[var(--text-secondary)]">From</span>
                            <input
                                type="date"
                                value={selectedFromDate}
                                onChange={(e) => setSelectedFromDate(e.target.value)}
                                className="min-w-0 flex-1 bg-transparent outline-none"
                            />
                        </label>

                        {/* TO DATE */}
                        <label className="flex items-center gap-2 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2.5">
                            <span className="text-xs font-black uppercase text-[var(--text-secondary)]">To</span>
                            <input
                                type="date"
                                value={selectedToDate}
                                onChange={(e) => setSelectedToDate(e.target.value)}
                                className="min-w-0 flex-1 bg-transparent outline-none"
                            />
                        </label>

                        {/* TEAM */}
                        <select
                            value={
                                teamFilter
                            }
                            onChange={(
                                e,
                            ) =>
                                setTeamFilter(
                                    e
                                        .target
                                        .value,
                                )
                            }
                            className="
                                px-4
                                py-2.5
                                rounded-xl
                                border
                                border-[var(--border-color)]
                                bg-[var(--bg-primary)]
                                outline-none
                            "
                        >

                            <option value="all">
                                All Teams
                            </option>

                            {uniqueTeams.map(
                                (
                                    t,
                                ) => (
                                    <option
                                        key={
                                            t
                                        }
                                        value={
                                            t
                                        }
                                    >
                                        {formatTeamName(t)}
                                    </option>
                                ),
                            )}

                        </select>

                        {/* EMPLOYEE */}
                        <select
                            value={
                                employeeFilter
                            }
                            onChange={(
                                e,
                            ) =>
                                setEmployeeFilter(
                                    e
                                        .target
                                        .value,
                                )
                            }
                            className="
                                px-4
                                py-2.5
                                rounded-xl
                                border
                                border-[var(--border-color)]
                                bg-[var(--bg-primary)]
                                outline-none
                            "
                        >

                            <option value="all">
                                All Employees
                            </option>

                            {uniqueEmployees.map(
                                (
                                    emp,
                                ) => (
                                    <option
                                        key={
                                            emp._id
                                        }
                                        value={
                                            emp._id
                                        }
                                    >
                                        {
                                            emp.name
                                        }
                                    </option>
                                ),
                            )}

                        </select>

                    </div>

                    {/* EXPORTS */}
                    <div
                        className="
                            flex
                            flex-wrap
                            gap-3
                            pt-1
                        "
                    >

                        <button
                            onClick={() =>
                                exportExcel(
                                    "all",
                                )
                            }
                            className="
                                inline-flex
                                items-center
                                gap-2
                                px-4
                                py-2
                                rounded-xl
                                bg-[var(--accent-primary)]
                                hover:bg-[var(--accent-hover)]
                                text-white
                                font-semibold
                                transition-all
                            "
                        >

                            <FileSpreadsheet size={18} />
                            Export All

                        </button>

                        <button
                            onClick={() =>
                                exportExcel(
                                    "team",
                                )
                            }
                            className="
                                inline-flex
                                items-center
                                gap-2
                                px-4
                                py-2
                                rounded-xl
                                border border-[var(--border-color)]
                                bg-[var(--bg-primary)]
                                hover:border-[var(--accent-primary)]
                                text-[var(--text-primary)]
                                font-semibold
                                transition-all
                            "
                        >

                            <Download size={18} />
                            Export Team

                        </button>

                        <button
                            onClick={() =>
                                exportExcel(
                                    "employee",
                                )
                            }
                            className="
                                inline-flex
                                items-center
                                gap-2
                                px-4
                                py-2
                                rounded-xl
                                border border-[var(--border-color)]
                                bg-[var(--bg-primary)]
                                hover:border-[var(--accent-primary)]
                                text-[var(--text-primary)]
                                font-semibold
                                transition-all
                            "
                        >

                            <Download size={18} />
                            Export Employee

                        </button>

                    </div>

                </div>

                <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
                {/* ABSENCE REGISTER */}
                <div
                    className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-5 py-4 transition hover:border-[var(--accent-primary)]/40"
                >
                    <button
                        type="button"
                        onClick={() => setIsAbsentListOpen(true)}
                        className="flex w-full flex-col gap-2 text-left sm:flex-row sm:items-center sm:justify-between"
                    >
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="h-5 w-1 rounded-full bg-red-500" />
                                <h2 className="text-base font-black">Absence register</h2>
                            </div>
                            <p className="text-xs text-[var(--text-secondary)]">
                                Employees without a recorded check-in from {attendanceRangeLabel}.
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                            <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-black text-emerald-600">
                                {presentEmployees.length} present
                            </span>
                            <span className="rounded-full bg-red-500/10 px-3 py-1 text-xs font-black text-red-500">
                                {absentEmployees.length} absent
                            </span>
                            {absentEmployeeDays.length !== absentEmployees.length && (
                                <span className="rounded-full bg-[var(--bg-secondary)]0/10 px-3 py-1 text-xs font-black text-[var(--text-secondary)]">
                                    {absentEmployeeDays.length} absent day(s)
                                </span>
                            )}
                            <ChevronDown
                                size={22}
                                className="-rotate-90 ml-1 text-[var(--text-secondary)]"
                            />
                        </div>
                    </button>
                </div>

                {/* EMPLOYEE ATTENDANCE SUMMARY */}
                <div className="w-full rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-5 py-4 transition hover:border-[var(--accent-primary)]/40">
                    <button
                        type="button"
                        onClick={() => setIsEmployeeSummaryOpen(true)}
                        className="flex w-full items-center justify-between gap-4 text-left"
                    >
                        <div>
                            <h2 className="text-base font-bold">Daily Attendance Summary</h2>
                            <p className="text-xs text-[var(--text-secondary)]">
                                {dailyAttendanceSummaries.length} date(s) - Click to view present and absent employee names.
                            </p>
                        </div>
                        <ChevronDown
                            size={22}
                            className="-rotate-90 shrink-0 text-[var(--text-secondary)]"
                        />
                    </button>
                </div>
                </div>

                {isAbsentListOpen && (
                    <div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-6"
                        onClick={() => setIsAbsentListOpen(false)}
                        role="presentation"
                    >
                        <div
                            className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="absence-register-title"
                        >
                            <div className="flex items-start justify-between gap-4 border-b border-[var(--border-color)] p-5">
                                <div>
                                    <h2 id="absence-register-title" className="text-xl font-black">Absence Register</h2>
                                    <p className="mt-1 text-sm text-[var(--text-secondary)]">
                                        Employees without a recorded check-in from {attendanceRangeLabel}.
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-black text-emerald-600">{presentEmployees.length} present</span>
                                        <span className="rounded-full bg-red-500/10 px-3 py-1 text-xs font-black text-red-500">{absentEmployees.length} absent</span>
                                        <span className="rounded-full bg-[var(--bg-secondary)]0/10 px-3 py-1 text-xs font-black text-[var(--text-secondary)]">{absentEmployeeDays.length} absent day(s)</span>
                                        <span className="rounded-full bg-[var(--accent-subtle)] px-3 py-1 text-xs font-black text-[var(--accent-primary)]">{approvedLeaveEmployeeDays.length} approved leave day(s)</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setIsAbsentListOpen(false)}
                                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--border-color)] text-[var(--text-secondary)] transition hover:bg-red-500/10 hover:text-red-500"
                                    aria-label="Close absence register"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                            <div className="overflow-y-auto p-4 sm:p-5">
                                {absenceRegisterEmployees.length ? (
                                    <div className="overflow-hidden rounded-xl border border-[var(--border-color)]">
                                        <div className="hidden grid-cols-[minmax(190px,1.3fr)_minmax(120px,.8fr)_minmax(150px,1fr)_minmax(170px,1.1fr)_120px] gap-3 border-b border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-2.5 text-[10px] font-black uppercase tracking-wider text-[var(--text-secondary)] lg:grid">
                                            <span>Employee</span><span>Department</span><span>Absent dates</span><span>Approved leave dates</span><span>Escalation</span>
                                        </div>
                                        <div className="divide-y divide-[var(--border-color)]">
                                            {absenceRegisterEmployees.map((employee) => {
                                                const employeeAbsentDates = absentEmployeeDays.filter(
                                                    (item) => String(item.employee?._id || item.employee) === String(employee._id),
                                                );
                                                const employeeLeaveDates = approvedLeaveEmployeeDays.filter(
                                                    (item) => String(item.employee?._id || item.employee) === String(employee._id),
                                                );
                                                return (
                                                    <div key={employee._id} className="grid gap-3 px-4 py-3 hover:bg-red-500/[0.025] lg:grid-cols-[minmax(190px,1.3fr)_minmax(120px,.8fr)_minmax(150px,1fr)_minmax(170px,1.1fr)_120px] lg:items-center lg:gap-3">
                                                        <div className="flex min-w-0 items-center gap-3">
                                                            <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-red-500/10 text-xs font-black text-red-500">{(employee.name || "?").trim().charAt(0).toUpperCase()}</div>
                                                            <div className="min-w-0">
                                                                <p className="truncate text-sm font-bold">{employee.name}</p>
                                                                <p className="text-[11px] font-semibold text-[var(--text-secondary)]">{employee.employeeId || "No employee ID"}</p>
                                                            </div>
                                                        </div>
                                                        <p className="truncate text-xs font-semibold text-[var(--text-secondary)]">{employee.team ? formatTeamName(employee.team) : "Unassigned"}</p>
                                                        <div className="flex flex-wrap gap-1">
                                                            {employeeAbsentDates.length ? employeeAbsentDates.map((item) => (
                                                                <span key={`absent-${employee._id}-${item.date}`} className="rounded-md bg-red-500/10 px-2 py-1 text-[10px] font-black text-red-500">
                                                                    {formatDate(item.date)}
                                                                </span>
                                                            )) : <span className="text-xs font-semibold text-[var(--text-secondary)]">—</span>}
                                                        </div>
                                                        <div className="flex flex-wrap gap-1">
                                                            {employeeLeaveDates.length ? employeeLeaveDates.map((item) => (
                                                                <span
                                                                    key={`leave-${employee._id}-${item.date}`}
                                                                    title={String(item.leaveType || "Approved leave")}
                                                                    className="rounded-md bg-[var(--accent-subtle)] px-2 py-1 text-[10px] font-black text-[var(--accent-primary)]"
                                                                >
                                                                    {formatDate(item.date)}
                                                                </span>
                                                            )) : <span className="text-xs font-semibold text-[var(--text-secondary)]">—</span>}
                                                        </div>
                                                        <div><EscalationBadge employee={employee} /></div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="rounded-xl border border-emerald-500/15 bg-emerald-500/5 px-4 py-8 text-center text-sm font-semibold text-emerald-600">No absent employees for the selected filters.</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {isEmployeeSummaryOpen && (
                    <div
                        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-6"
                        onClick={() => setIsEmployeeSummaryOpen(false)}
                        role="presentation"
                    >
                        <div
                            className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl"
                            onClick={(event) => event.stopPropagation()}
                            role="dialog"
                            aria-modal="true"
                            aria-labelledby="employee-attendance-summary-title"
                        >
                            <div className="flex items-start justify-between gap-4 border-b border-[var(--border-color)] p-5">
                                <div>
                                    <h2 id="employee-attendance-summary-title" className="text-xl font-black">
                                        Daily Attendance Summary
                                    </h2>
                                    <p className="text-sm text-[var(--text-secondary)]">
                                        Date-wise present and absent employee names from {attendanceRangeLabel}.
                                    </p>
                                    <div className="mt-3 flex flex-wrap gap-3">
                                        <label className="text-xs font-bold text-[var(--text-secondary)]">
                                            From
                                            <input
                                                type="date"
                                                value={selectedFromDate}
                                                onChange={(event) => setSelectedFromDate(event.target.value)}
                                                className="ml-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-primary)]"
                                            />
                                        </label>
                                        <label className="text-xs font-bold text-[var(--text-secondary)]">
                                            To
                                            <input
                                                type="date"
                                                value={selectedToDate}
                                                onChange={(event) => setSelectedToDate(event.target.value)}
                                                className="ml-2 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent-primary)]"
                                            />
                                        </label>
                                    </div>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={exportDailyAttendanceSummary}
                                        disabled={!dailyAttendanceSummaries.length}
                                        className="inline-flex h-10 items-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-black text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <FileSpreadsheet size={17} />
                                        Export Excel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsEmployeeSummaryOpen(false)}
                                        className="grid h-10 w-10 place-items-center rounded-full border border-[var(--border-color)] text-[var(--text-secondary)] transition hover:bg-red-500/10 hover:text-red-500"
                                        aria-label="Close employee attendance summary"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>
                            </div>

                            <div className="overflow-y-auto p-5">
                                {dailyAttendanceSummaries.length ? (
                                    <div className="grid gap-4">
                                        {dailyAttendanceSummaries.map(({
                                            date,
                                            presentEmployees: dayPresent,
                                            absentEmployees: dayAbsent,
                                            leaveEmployees: dayOnLeave,
                                        }) => (
                                            <section
                                                key={date}
                                                className="overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)]"
                                            >
                                                <div className="border-b border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-3">
                                                    <h3 className="font-black text-[var(--text-primary)]">{formatDate(date)}</h3>
                                                    <p className="text-xs font-semibold text-[var(--text-secondary)]">
                                                        {dayPresent.length + dayAbsent.length + dayOnLeave.length} employees recorded
                                                    </p>
                                                </div>

                                                <div className="grid md:grid-cols-3">
                                                    <div className="border-b border-[var(--border-color)] p-4 md:border-b-0 md:border-r">
                                                        <div className="mb-3 flex items-center justify-between gap-2">
                                                            <h4 className="font-black uppercase tracking-wide text-emerald-600">Present</h4>
                                                            <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-black text-emerald-600">
                                                                {dayPresent.length}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {dayPresent.length ? dayPresent.map((employee) => (
                                                                <span
                                                                    key={employee?._id || employee?.employeeId || employee?.name}
                                                                    className="rounded-lg border border-emerald-500/15 bg-emerald-500/5 px-3 py-2 text-xs font-bold text-[var(--text-primary)]"
                                                                >
                                                                    {employee?.name || "Unknown employee"}
                                                                </span>
                                                            )) : (
                                                                <p className="text-sm font-semibold text-[var(--text-secondary)]">No employees present.</p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="border-b border-[var(--border-color)] p-4 md:border-b-0 md:border-r">
                                                        <div className="mb-3 flex items-center justify-between gap-2">
                                                            <h4 className="font-black uppercase tracking-wide text-red-500">Absent</h4>
                                                            <span className="rounded-full bg-red-500/10 px-3 py-1 text-xs font-black text-red-500">
                                                                {dayAbsent.length}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {dayAbsent.length ? dayAbsent.map((employee) => (
                                                                <span
                                                                    key={employee?._id || employee?.employeeId || employee?.name}
                                                                    className="rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-2 text-xs font-bold text-red-600"
                                                                >
                                                                    {employee?.name || "Unknown employee"}
                                                                </span>
                                                            )) : (
                                                                <p className="text-sm font-semibold text-[var(--text-secondary)]">No employees absent.</p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="p-4">
                                                        <div className="mb-3 flex items-center justify-between gap-2">
                                                            <h4 className="font-black uppercase tracking-wide text-amber-600">On Leave</h4>
                                                            <span className="rounded-full bg-amber-500/10 px-3 py-1 text-xs font-black text-amber-600">
                                                                {dayOnLeave.length}
                                                            </span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-2">
                                                            {dayOnLeave.length ? dayOnLeave.map((employee) => (
                                                                <span
                                                                    key={employee?._id || employee?.employeeId || employee?.name}
                                                                    className="rounded-lg border border-amber-500/15 bg-amber-500/5 px-3 py-2 text-xs font-bold text-[var(--text-primary)]"
                                                                >
                                                                    {employee?.name || "Unknown employee"}
                                                                    <span className="ml-1 font-semibold capitalize text-amber-600">
                                                                        ({employee?.leaveType === "sick" ? "Medical" : employee?.leaveType || "Approved"})
                                                                    </span>
                                                                </span>
                                                            )) : (
                                                                <p className="text-sm font-semibold text-[var(--text-secondary)]">No employees on approved leave.</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </section>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="rounded-xl border border-[var(--border-color)] px-3 py-3 text-sm font-semibold text-[var(--text-secondary)]">
                                        No attendance is available for the selected date range and filters.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {tableDateKeys.length > 1 && (
                    <div className="flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex min-w-0 items-center gap-3">
                            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[var(--accent-primary)]/10 text-sm font-black text-[var(--accent-primary)]">
                                {activeTableDateIndex + 1}
                            </div>
                            <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-wider text-[var(--text-secondary)]">
                                Day {activeTableDateIndex + 1} of {tableDateKeys.length}
                            </p>
                            <h2 className="mt-0.5 text-base font-black">{formatDate(activeTableDate)}</h2>
                            <p className="text-xs text-[var(--text-secondary)]">
                                {pagedTableRecords.length} attendance record(s)
                            </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            {tableDateKeys.length > 1 && (
                                <select
                                    value={activeTableDate}
                                    onChange={(event) => setSelectedTableDate(event.target.value)}
                                    className="h-9 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold outline-none focus:border-[var(--accent-primary)]"
                                    aria-label="Jump to attendance date"
                                >
                                    {tableDateKeys.map((dateKey, index) => (
                                        <option key={dateKey} value={dateKey}>
                                            Day {index + 1} – {formatDate(dateKey)}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {tableDateKeys.length > 1 && (
                                <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    disabled={activeTableDateIndex === 0}
                                    onClick={() => setSelectedTableDate(tableDateKeys[activeTableDateIndex - 1])}
                                    className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 py-2 text-xs font-bold disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    ← Previous day
                                </button>
                                <button
                                    type="button"
                                    disabled={activeTableDateIndex >= tableDateKeys.length - 1}
                                    onClick={() => setSelectedTableDate(tableDateKeys[activeTableDateIndex + 1])}
                                    className="rounded-lg bg-[var(--accent-primary)] px-4 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
                                >
                                    Next day →
                                </button>
                            </div>
                            )}
                        </div>
                    </div>
                )}

                {/* MOBILE CARDS */}
                <div className="space-y-3 md:hidden">
                    {pagedTableRecords.map((r, index) => {
                        const isAbsentPlaceholder = r.isAbsentPlaceholder;
                        const isOnBreak = r.breakStart && !r.breakEnd && !r.checkOut;
                        const lunchDurationMinutes = getLunchDurationMinutes(r);
                        const longLunch = lunchDurationMinutes > LUNCH_LIMIT_MINUTES;
                        const statusLabel = isAbsentPlaceholder
                            ? "Absent"
                            : r.checkOut
                                ? "Completed"
                                : isOnBreak
                                    ? "At Lunch"
                                    : r.checkIn
                                        ? "Working"
                                        : "Absent";
                        const statusClass = statusLabel === "Completed"
                            ? "bg-emerald-500/15 text-emerald-500"
                            : statusLabel === "At Lunch"
                                ? "bg-amber-500/15 text-amber-500"
                                : statusLabel === "Working"
                                    ? "bg-[var(--accent-primary)]/15 text-[var(--accent-primary)]"
                                    : "bg-red-500/15 text-red-500";

                        return (
                            <div
                                key={r._id || `${r.employee?._id || r.employee?.employeeId || "attendance"}-${r.date}-${index}`}
                                className={`rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-[var(--shadow-soft)] ${isAbsentPlaceholder ? "border-red-500/20 bg-red-500/5" : ""}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <h3 className="break-words text-sm font-black text-[var(--text-primary)]">
                                            {r.employee?.name || "Employee"}
                                        </h3>
                                        <p className="mt-0.5 text-xs font-semibold text-[var(--text-secondary)]">
                                            {r.employee?.employeeId || "-"} · {formatTeamName(r.employee?.team)}
                                        </p>
                                        <div className="mt-2">
                                            <EscalationBadge employee={r.employee} />
                                        </div>
                                        <p className="mt-1 text-xs font-bold text-[var(--text-secondary)]">
                                            {r.displayDate || formatDate(r.date)} · {isAbsentPlaceholder ? "No mode" : r.mode || "-"}
                                        </p>
                                    </div>
                                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-black uppercase ${statusClass}`}>
                                        {statusLabel}
                                    </span>
                                </div>

                                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                    <InfoTile label="Check In" value={formatRecordTime(r, "checkIn")} />
                                    <InfoTile label="Check Out" value={renderAttendanceCheckout(r)} />
                                    <InfoTile label="Lunch Start" value={formatRecordTime(r, "breakStart")} />
                                    <InfoTile label="Lunch End" value={r.breakEnd ? formatRecordTime(r, "breakEnd") : isOnBreak ? "Running" : "-"} />
                                    <InfoTile
                                        label="Lunch Duration"
                                        value={isOnBreak ? "At Lunch" : renderLunchDuration(r)}
                                        tone={longLunch ? "text-amber-600" : ""}
                                    />
                                    <InfoTile label="Hours" value={renderAttendanceHours(r)} />
                                </div>

                                {r.earlyCheckoutReason && (
                                    <div className="mt-3 rounded-xl border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs">
                                        <p className="font-black uppercase tracking-wide text-amber-600 mb-1">Early Checkout Reason</p>
                                        <p className="text-amber-800 dark:text-amber-300 font-medium">
                                            {r.earlyCheckoutReason}
                                        </p>
                                        {r.earlyCheckoutReasonSubmittedAt && (
                                            <p className="text-[10px] text-amber-600/70 mt-1">
                                                Submitted at {new Date(r.earlyCheckoutReasonSubmittedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {r.salesCheckInProof?.photoData && (
                                    <div className="mt-3 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 py-2 text-xs">
                                        <div className="flex items-center justify-between gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setProofModalData(r)}
                                                className="inline-flex items-center gap-1 font-black text-[var(--accent-primary)] hover:underline cursor-pointer"
                                            >
                                                <Camera size={13} />
                                                <span>View photo</span>
                                            </button>
                                            {r.salesCheckInProof.source === 'selfie' && (
                                                <span className="rounded bg-[var(--accent-subtle)] text-[var(--accent-primary)] dark:text-[var(--accent-primary)] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider border border-[var(--accent-primary)]/20">
                                                    Selfie
                                                </span>
                                            )}
                                        </div>
                                        {r.salesCheckInProof.description && (
                                            <p className="mt-1 font-semibold text-neutral-800 dark:text-neutral-200">
                                                "{r.salesCheckInProof.description}"
                                            </p>
                                        )}
                                        {r.salesCheckInProof.location?.latitude && r.salesCheckInProof.location?.longitude && (
                                            <div className="mt-1 flex flex-col gap-0.5 text-[11px] text-[var(--text-secondary)]">
                                                <span>
                                                    <ReverseGeocode latitude={r.salesCheckInProof.location.latitude} longitude={r.salesCheckInProof.location.longitude} />
                                                </span>
                                                <a
                                                    href={`https://www.google.com/maps?q=${r.salesCheckInProof.location.latitude},${r.salesCheckInProof.location.longitude}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="font-mono text-[10px] opacity-80 hover:opacity-100 hover:text-rose-500 hover:underline flex items-center gap-1 cursor-pointer w-fit"
                                                >
                                                    <MapPin size={9} className="text-rose-500 shrink-0" />
                                                    <span>GPS: {Number(r.salesCheckInProof.location.latitude).toFixed(4)}, {Number(r.salesCheckInProof.location.longitude).toFixed(4)}</span>
                                                    <ExternalLink size={9} className="text-rose-500" />
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!isAbsentPlaceholder && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {shouldShowEscalationMail(r) && (
                                            <button
                                                type="button"
                                                onClick={() => handleSendEscalationMail(r)}
                                                disabled={escalationSendingId === r._id}
                                                className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border border-red-500/20 bg-red-500/10 px-3 text-xs font-bold text-red-500 disabled:opacity-50"
                                            >
                                                <Mail size={13} /> Mail
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={() => handleEditClick(r)}
                                            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold text-[var(--text-primary)]"
                                        >
                                            <Edit2 size={13} /> Edit
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* PAGINATION & ROWS PER PAGE TOOLBAR */}
                <div className="flex flex-col gap-3 rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between shadow-sm">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[var(--text-secondary)]">Show per page:</span>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                const val = e.target.value === 'all' ? 'all' : Number(e.target.value);
                                setPageSize(val);
                                setCurrentPage(1);
                            }}
                            className="h-8 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-2.5 text-xs font-black outline-none focus:border-[var(--accent-primary)] cursor-pointer"
                        >
                            <option value={10}>10 employees</option>
                            <option value={50}>50 employees</option>
                            <option value={100}>100 employees</option>
                            <option value="all">All employees</option>
                        </select>
                        <span className="text-xs font-semibold text-[var(--text-secondary)] ml-2">
                            Showing {pagedTableRecords.length === 0 ? 0 : pageSize === 'all' ? 1 : (currentPage - 1) * Number(pageSize) + 1} - {pageSize === 'all' ? pagedTableRecords.length : Math.min(pagedTableRecords.length, currentPage * Number(pageSize))} of {pagedTableRecords.length} records
                        </span>
                    </div>

                    {pageSize !== 'all' && totalPages > 1 && (
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                disabled={currentPage <= 1}
                                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                className="h-8 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--border-color)] transition"
                            >
                                ← Prev
                            </button>
                            <span className="text-xs font-black px-2">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                type="button"
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                className="h-8 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-3 text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[var(--border-color)] transition"
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </div>

                {/* TABLE */}
                <div
                    ref={attendanceTableScrollRef}
                    className="ui-table-shell hidden rounded-xl md:block border border-[var(--border-color)] shadow-sm"
                >

                    <table
                        ref={attendanceTableRef}
                        className="
                            ui-table
                            w-full
                            table-fixed
                            text-[11px]
                            xl:text-xs
                        "
                    >
                        <colgroup>
                            <col className="w-[12%]" />
                            <col className="w-[10%]" />
                            <col className="w-[4%]" />
                            <col className="w-[8%]" />
                            <col className="w-[6%]" />
                            <col className="w-[8%]" />
                            <col className="w-[10%]" />
                            <col className="w-[8%]" />
                            <col className="w-[8%]" />
                            <col className="w-[8%]" />
                            <col className="w-[9%]" />
                            <col className="w-[9%]" />
                        </colgroup>

                        <thead className="sticky top-0 bg-[var(--bg-primary)] z-10 shadow-sm border-b border-[var(--border-color)]">

                            <tr>

                                {[
                                    "Employee",
                                    "Team",
                                    "Esc",
                                    "Date",
                                    "Mode",
                                    "Check In",
                                    "Lunch",
                                    "Check Out",
                                    "Hours",
                                    "Status",
                                    "Proof",
                                    "Actions",
                                ].map(
                                    (
                                        head,
                                    ) => (
                                        <th
                                            key={head}
                                            className="
                                                px-2
                                                py-2
                                                leading-tight
                                                whitespace-normal
                                            "
                                        >
                                            {head}
                                        </th>
                                    ),
                                )}

                            </tr>

                        </thead>

                        <tbody>

                            {attendanceLoading && pagedTableRecords.length === 0 && Array.from({ length: 6 }, (_, rowIndex) => (
                                <tr key={`attendance-loading-${rowIndex}`} aria-hidden="true">
                                    {Array.from({ length: 14 }, (_, cellIndex) => (
                                        <td key={cellIndex} className="px-2 py-3">
                                            <div className="h-3 w-3/4 animate-pulse rounded-full bg-neutral-200/80 dark:bg-neutral-700/70" />
                                        </td>
                                    ))}
                                </tr>
                            ))}

                            {displayedTableRecords.map(
                                (
                                    r,
                                    index,
                                ) => {

                                    const isAbsentPlaceholder =
                                        r.isAbsentPlaceholder;

                                    const isOnBreak =
                                        r.breakStart &&
                                        !r.breakEnd &&
                                        !r.checkOut;

                                    const lunchDurationMinutes =
                                        getLunchDurationMinutes(r);

                                    const longLunch =
                                        lunchDurationMinutes >
                                        LUNCH_LIMIT_MINUTES;

                                    return (
                                        <tr
                                            key={index}
                                            className={`
                                                border-b
                                                border-[var(--border-color)]
                                                ${isAbsentPlaceholder ? "bg-red-500/5" : ""}
                                                hover:bg-[var(--bg-primary)]
                                                transition-all
                                            `}
                                        >

                                            {/* EMPLOYEE */}
                                            <td
                                                className="
                                                    px-2
                                                    py-1.5
                                                "
                                            >
                                                <div
                                                    className="
                                                        flex
                                                        flex-col
                                                        min-w-0
                                                    "
                                                >

                                                    <span
                                                        className="
                                                            font-semibold
                                                            truncate
                                                        "
                                                        title={r.employee?.name}
                                                    >
                                                        {r.employee?.name}
                                                    </span>

                                                    <span
                                                        className="
                                                            text-xs
                                                            text-[var(--text-secondary)]
                                                            truncate
                                                        "
                                                    >
                                                        {r.employee?.employeeId}
                                                    </span>

                                                </div>
                                            </td>

                                            {/* TEAM */}
                                            <td
                                                className="
                                                    px-2
                                                    py-1.5
                                                    break-words
                                                "
                                                title={formatTeamName(r.employee?.team)}
                                            >
                                                {formatTeamName(r.employee?.team)}
                                            </td>

                                            {/* ESCALATIONS */}
                                            <td
                                                className="
                                                    px-2
                                                    py-1.5
                                                    whitespace-nowrap
                                                "
                                            >
                                                <EscalationBadge employee={r.employee} compact />
                                            </td>

                                            {/* DATE */}
                                            <td
                                                className="
                                                    px-2
                                                    py-1.5
                                                    whitespace-nowrap
                                                "
                                            >
                                                {r.displayDate || formatDate(r.date)}
                                            </td>

                                            {/* MODE */}
                                            <td
                                                className="
                                                    px-2
                                                    py-1.5
                                                    whitespace-nowrap
                                                "
                                            >

                                                {isAbsentPlaceholder ? (
                                                    "—"
                                                ) : (
                                                    <span
                                                        className={`
                                                            inline-flex
                                                            items-center
                                                            px-2
                                                            py-1
                                                            rounded-full
                                                            text-xs
                                                            font-semibold
                                                            ${r.mode === "wfh"
                                                                ? "bg-[var(--accent-primary)]/15 text-blue-400"
                                                                : "bg-emerald-500/15 text-emerald-400"
                                                            }
                                                        `}
                                                    >
                                                        {r.mode}
                                                    </span>
                                                )}

                                            </td>

                                            {/* CHECK IN */}
                                            <td
                                                className="
                                                    px-2
                                                    py-1.5
                                                    whitespace-nowrap
                                                "
                                            >
                                                {formatRecordTime(r, "checkIn")}
                                            </td>

                                            {/* LUNCH */}
                                            <td className="px-2 py-1.5 whitespace-nowrap">
                                                {isOnBreak ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="inline-flex items-center gap-1 font-bold text-amber-600 dark:text-amber-400">
                                                            <Coffee size={11} /> At Lunch
                                                        </span>
                                                        <span className="text-[10px] text-[var(--text-secondary)] font-medium">
                                                            {formatRecordTime(r, "breakStart")} - Running
                                                        </span>
                                                    </div>
                                                ) : (lunchDurationMinutes > 0 || r.breakStart) ? (
                                                    <div className="flex flex-col gap-0.5">
                                                        <div className="flex items-center gap-1">
                                                            <span className="font-bold text-[var(--text-primary)]">
                                                                {formatLunchDuration(lunchDurationMinutes)}
                                                            </span>
                                                            {longLunch && (
                                                                <span className="rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[9px] font-black text-amber-600 dark:text-amber-300">
                                                                    Over 45m
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className="text-[10px] text-[var(--text-secondary)] font-medium">
                                                            {formatRecordTime(r, "breakStart")} - {r.breakEnd ? formatRecordTime(r, "breakEnd") : "—"}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    "—"
                                                )}
                                            </td>

                                            {/* CHECK OUT */}
                                            <td className="px-2 py-1.5 whitespace-nowrap">
                                                {renderAttendanceCheckout(r)}
                                            </td>

                                            {/* HOURS */}
                                            <td className="px-2 py-1.5 whitespace-nowrap">
                                                {renderAttendanceHours(r)}
                                            </td>

                                            {/* STATUS */}
                                            <td
                                                className="
                                                    px-2
                                                    py-1.5
                                                "
                                            >

                                                {isAbsentPlaceholder ? (

                                                    <span
                                                        className="
                                                            inline-flex
                                                            items-center
                                                            px-2
                                                            py-1.5
                                                            rounded-full
                                                            bg-red-500/15
                                                            text-red-400
                                                            text-xs
                                                            font-semibold
                                                        "
                                                    >
                                                        Absent
                                                    </span>

                                                ) : r.checkOut ? (

                                                    <span
                                                        className="
                                                            inline-flex
                                                            items-center
                                                            px-2
                                                            py-1.5
                                                            rounded-full
                                                            bg-emerald-500/15
                                                            text-emerald-400
                                                            text-xs
                                                            font-semibold
                                                        "
                                                    >
                                                        Completed
                                                    </span>

                                                ) : isOnBreak ? (

                                                    <span
                                                        className="
                                                            inline-flex
                                                            items-center
                                                            px-2
                                                            py-1.5
                                                            rounded-full
                                                            bg-amber-500/15
                                                            text-amber-400
                                                            text-xs
                                                            font-semibold
                                                        "
                                                    >
                                                        At Lunch
                                                    </span>

                                                ) : r.checkIn ? (

                                                    <span
                                                        className="
                                                            inline-flex
                                                            items-center
                                                            px-2
                                                            py-1.5
                                                            rounded-full
                                                            bg-[var(--accent-primary)]/15
                                                            text-blue-400
                                                            text-xs
                                                            font-semibold
                                                        "
                                                    >
                                                        Working
                                                    </span>

                                                ) : (

                                                    <span
                                                        className="
                                                            inline-flex
                                                            items-center
                                                            px-2
                                                            py-1.5
                                                            rounded-full
                                                            bg-red-500/15
                                                            text-red-400
                                                            text-xs
                                                            font-semibold
                                                        "
                                                    >
                                                        Absent
                                                    </span>

                                                )}

                                            </td>

                                            {/* PROOF */}
                                            <td className="px-2 py-2 text-xs align-top">
                                                {r.salesCheckInProof?.photoData ? (
                                                    <div className="flex flex-col gap-1 w-full max-w-full overflow-hidden">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <button
                                                                type="button"
                                                                onClick={() => setProofModalData(r)}
                                                                className="inline-flex items-center gap-1 font-bold text-xs text-[var(--accent-primary)] hover:text-[var(--accent-hover)] transition-colors cursor-pointer w-fit group shrink-0"
                                                            >
                                                                <Camera size={12} className="text-[var(--accent-primary)]" />
                                                                <span className="underline underline-offset-2">View selfie</span>
                                                            </button>
                                                            {r.salesCheckInProof.source === 'selfie' && (
                                                                <span className="rounded bg-[var(--accent-subtle)] text-[var(--accent-primary)] dark:text-[var(--accent-primary)] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider border border-[var(--accent-primary)]/20 shrink-0">
                                                                    Selfie
                                                                </span>
                                                            )}
                                                        </div>

                                                        {r.salesCheckInProof.location?.latitude && r.salesCheckInProof.location?.longitude ? (
                                                            <div className="flex flex-col gap-0.5 text-[11px] text-[var(--text-secondary)] min-w-0">
                                                                <div className="truncate text-[11px] leading-tight font-medium" title="Check-in Location">
                                                                    <ReverseGeocode latitude={r.salesCheckInProof.location.latitude} longitude={r.salesCheckInProof.location.longitude} />
                                                                </div>
                                                                <a
                                                                    href={`https://www.google.com/maps?q=${r.salesCheckInProof.location.latitude},${r.salesCheckInProof.location.longitude}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="inline-flex items-center gap-1 text-[10px] text-rose-500 hover:text-rose-600 font-mono font-medium hover:underline cursor-pointer truncate max-w-full"
                                                                    title="Open in Google Maps"
                                                                >
                                                                    <MapPin size={10} className="shrink-0 text-rose-500" />
                                                                    <span className="truncate">GPS: {Number(r.salesCheckInProof.location.latitude).toFixed(4)}, {Number(r.salesCheckInProof.location.longitude).toFixed(4)}</span>
                                                                    <ExternalLink size={9} className="opacity-75 shrink-0" />
                                                                </a>
                                                            </div>
                                                        ) : (
                                                            <span className="text-[10px] text-[var(--text-secondary)] opacity-60">No GPS location</span>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-[var(--text-secondary)] opacity-40">—</span>
                                                )}
                                            </td>

                                            {/* ACTIONS */}
                                            <td
                                                className="
                                                    px-2
                                                    py-2.5
                                                    whitespace-nowrap
                                                "
                                            >
                                                {isAbsentPlaceholder ? (
                                                    <span className="text-xs font-semibold text-[var(--text-secondary)]">—</span>
                                                ) : (
                                                <div className="ui-action-row">
                                                    {shouldShowEscalationMail(r) && (
                                                        <button
                                                            type="button"
                                                            title="Send attendance escalation mail"
                                                            onClick={() => handleSendEscalationMail(r)}
                                                            disabled={escalationSendingId === r._id}
                                                            className="ui-icon-button ui-button-danger disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            <Mail size={12} />
                                                        </button>
                                                    )}

                                                    {[
                                                        "checkIn",
                                                        "checkOut",
                                                        "breakStart",
                                                        "breakEnd",
                                                    ].filter((field) => Boolean(r[field])).map((field) => {
                                                        const busyKey = `${r._id}-${field}`;
                                                        return (
                                                            <button
                                                                key={field}
                                                                type="button"
                                                                title={`Revert ${attendanceActionLabels[field]}`}
                                                                onClick={() => handleRevertAttendanceAction(r, field)}
                                                                disabled={revertingAction === busyKey}
                                                                className="ui-icon-button ui-button-warning disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                <Undo2 size={12} />
                                                            </button>
                                                        );
                                                    })}

                                                    <button
                                                        type="button"
                                                        onClick={() => handleEditClick(r)}
                                                        title="Edit attendance"
                                                        className="ui-icon-button ui-button-secondary hover:border-[var(--accent-primary)] hover:text-[var(--accent-primary)]"
                                                    >
                                                        <Edit2 size={12} />
                                                    </button>
                                                </div>
                                                )}
                                            </td>

                                        </tr>
                                    );
                                },
                            )}

                        </tbody>

                    </table>

                </div>

            </div>

            {/* ================= EDIT ATTENDANCE MODAL ================= */}
            {isEditModalOpen && editingRecord && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div
                        onClick={() => {
                            setIsEditModalOpen(false);
                            setEditingRecord(null);
                        }}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                    />

                    {/* Modal Box */}
                    <div className="relative w-full max-w-lg bg-[var(--card-bg)] dark:bg-[#121214] border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl p-6 md:p-8 flex flex-col justify-between transition-transform duration-300 z-10 animate-fade-in max-h-[90vh] overflow-y-auto">

                        {/* Floating Close Button */}
                        <button
                            onClick={() => {
                                setIsEditModalOpen(false);
                                setEditingRecord(null);
                            }}
                            className="absolute -top-14 right-0 md:-right-14 w-11 h-11 rounded-full bg-[var(--card-bg)]/10 dark:bg-black/20 hover:bg-[var(--card-bg)]/20 dark:hover:bg-black/35 text-white dark:text-neutral-300 backdrop-blur-md flex items-center justify-center transition-all duration-200 border border-white/20 dark:border-neutral-800 hover:scale-110 shadow-xl cursor-pointer"
                            title="Close"
                        >
                            <X size={22} />
                        </button>

                        {/* Header */}
                        <div className="pb-4 border-b border-neutral-100 dark:border-neutral-800 mb-6">
                            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Edit Attendance Record</h2>
                            <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">
                                Modify attendance for <span className="font-semibold text-[var(--accent-primary)]">{editingRecord.employee?.name}</span>
                            </p>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleEditSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Date</label>
                                    <input
                                        type="date"
                                        value={editForm.date}
                                        onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
                                        required
                                        className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Status</label>
                                    <select
                                        value={editForm.status}
                                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                                        required
                                        className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all text-neutral-700 dark:text-neutral-300"
                                    >
                                        <option value="present">Present</option>
                                        <option value="absent">Absent</option>
                                        <option value="late">Late</option>
                                        <option value="half-day">Half Day</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Mode</label>
                                    <select
                                        value={editForm.mode}
                                        onChange={(e) => setEditForm({ ...editForm, mode: e.target.value })}
                                        required
                                        className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all text-neutral-700 dark:text-neutral-300"
                                    >
                                        <option value="office">Office</option>
                                        <option value="wfh">WFH</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Check-In Time</label>
                                    <CustomTimePicker
                                        value={editForm.checkIn}
                                        onChange={(e) => setEditForm({ ...editForm, checkIn: e.target.value })}
                                        placeholder="09:30 AM"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Lunch Start</label>
                                    <CustomTimePicker
                                        value={editForm.breakStart}
                                        onChange={(e) => setEditForm({ ...editForm, breakStart: e.target.value })}
                                        placeholder="01:30 PM"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Lunch End</label>
                                    <CustomTimePicker
                                        value={editForm.breakEnd}
                                        onChange={(e) => setEditForm({ ...editForm, breakEnd: e.target.value })}
                                        placeholder="02:15 PM"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Check-Out Time</label>
                                    <CustomTimePicker
                                        value={editForm.checkOut}
                                        onChange={(e) => setEditForm({ ...editForm, checkOut: e.target.value })}
                                        placeholder="06:30 PM"
                                    />
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center justify-end gap-3 pt-6 border-t border-neutral-100 dark:border-neutral-800 mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditModalOpen(false);
                                        setEditingRecord(null);
                                    }}
                                    className="px-6 py-2.5 rounded-lg bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-8 py-2.5 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white font-semibold transition-all shadow-lg shadow-[var(--accent-primary)]/20 text-sm"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ================= MANUAL ATTENDANCE MODAL ================= */}
            {isManualModalOpen && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
                    <div
                        onClick={() => setIsManualModalOpen(false)}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
                    />

                    <div className="relative w-full max-w-lg bg-[var(--card-bg)] dark:bg-[#121214] border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl p-6 md:p-8 flex flex-col justify-between transition-transform duration-300 z-10 animate-fade-in max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={() => setIsManualModalOpen(false)}
                            className="absolute -top-14 right-0 md:-right-14 w-11 h-11 rounded-full bg-[var(--card-bg)]/10 dark:bg-black/20 hover:bg-[var(--card-bg)]/20 dark:hover:bg-black/35 text-white dark:text-neutral-300 backdrop-blur-md flex items-center justify-center transition-all duration-200 border border-white/20 dark:border-neutral-800 hover:scale-110 shadow-xl cursor-pointer"
                            title="Close"
                        >
                            <X size={22} />
                        </button>

                        <div className="pb-4 border-b border-neutral-100 dark:border-neutral-800 mb-6">
                            <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">Manual Attendance Entry</h2>
                            <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">
                                Add attendance for an employee when it was missed or entered outside the system.
                            </p>
                        </div>

                        <form onSubmit={handleManualSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5 md:col-span-2">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Employee</label>
                                    <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-3">
                                        <div className="mb-3 flex items-center justify-between gap-3">
                                            <div>
                                                <span className="text-sm font-black text-neutral-800 dark:text-neutral-200">
                                                    {manualForm.employeeIds.length} selected
                                                </span>
                                                <p className="text-[11px] font-semibold text-neutral-400">Choose one or more employees</p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setManualForm({ ...manualForm, employeeIds: manualEmployeeOptions.map((employee) => employee._id) })}
                                                    className="rounded-lg bg-[var(--accent-primary)]/10 px-2.5 py-1.5 text-xs font-black text-[var(--accent-primary)]"
                                                >
                                                    Select all
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setManualForm({ ...manualForm, employeeIds: [] })}
                                                    className="rounded-lg bg-neutral-100 px-2.5 py-1.5 text-xs font-black text-neutral-500 hover:bg-neutral-200 dark:bg-neutral-900 dark:hover:bg-neutral-800"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>

                                        {manualForm.employeeIds.length > 0 && (
                                            <div className="mb-3 flex max-h-16 flex-wrap gap-1.5 overflow-y-auto rounded-xl border border-[var(--accent-primary)]/15 bg-[var(--accent-primary)]/5 p-2">
                                                {manualEmployeeOptions
                                                    .filter((employee) => manualForm.employeeIds.includes(employee._id))
                                                    .map((employee) => (
                                                        <button
                                                            key={employee._id}
                                                            type="button"
                                                            onClick={() => setManualForm({
                                                                ...manualForm,
                                                                employeeIds: manualForm.employeeIds.filter((id) => id !== employee._id),
                                                            })}
                                                            className="inline-flex items-center gap-1 rounded-full bg-[var(--card-bg)] px-2.5 py-1 text-xs font-bold text-[var(--accent-primary)] shadow-sm ring-1 ring-[var(--accent-primary)]/15 dark:bg-neutral-950"
                                                            title="Remove employee"
                                                        >
                                                            {employee.name}
                                                            <X size={12} />
                                                        </button>
                                                    ))}
                                            </div>
                                        )}

                                        <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                                            {manualEmployeeOptions.map((employee) => {
                                                const checked = manualForm.employeeIds.includes(employee._id);
                                                return (
                                                    <button
                                                        key={employee._id}
                                                        type="button"
                                                        onClick={() => {
                                                            const nextIds = checked
                                                                ? manualForm.employeeIds.filter((id) => id !== employee._id)
                                                                : [...manualForm.employeeIds, employee._id];
                                                            setManualForm({ ...manualForm, employeeIds: nextIds });
                                                        }}
                                                        className={`flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all ${
                                                            checked
                                                                ? "border-[var(--accent-primary)] bg-[var(--accent-primary)]/10 shadow-sm"
                                                                : "border-transparent bg-[var(--card-bg)] hover:border-[var(--border-color)] hover:bg-[var(--bg-secondary)] dark:bg-neutral-950"
                                                        }`}
                                                    >
                                                        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-black ${
                                                            checked
                                                                ? "bg-[var(--accent-primary)] text-white"
                                                                : "bg-neutral-100 text-neutral-500 dark:bg-neutral-900"
                                                        }`}>
                                                            {checked ? <Check size={15} /> : employee.name?.charAt(0) || "E"}
                                                        </span>
                                                        <span className="min-w-0 flex-1 text-sm font-bold text-neutral-700 dark:text-neutral-300">
                                                            <span className="block truncate">{employee.name}</span>
                                                            <span className="block truncate text-xs font-semibold text-neutral-400">
                                                                {employee.employeeId || "-"} {employee.team ? `- ${formatTeamName(employee.team)}` : ""}
                                                            </span>
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    {!manualEmployeeOptions.length && (
                                        <p className="text-xs font-semibold text-red-500">No employees available. Please refresh once or check employee records.</p>
                                    )}
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">From Date</label>
                                    <input
                                        type="date"
                                        value={manualForm.fromDate}
                                        onChange={(e) => {
                                            const nextFromDate = e.target.value;
                                            setManualForm({
                                                ...manualForm,
                                                fromDate: nextFromDate,
                                                toDate: manualForm.toDate && manualForm.toDate >= nextFromDate ? manualForm.toDate : nextFromDate,
                                            });
                                        }}
                                        required
                                        className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">To Date</label>
                                    <input
                                        type="date"
                                        value={manualForm.toDate}
                                        min={manualForm.fromDate}
                                        onChange={(e) => setManualForm({ ...manualForm, toDate: e.target.value })}
                                        required
                                        className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all"
                                    />
                                </div>

                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Status</label>
                                    <select
                                        value={manualForm.status}
                                        onChange={(e) => setManualForm({ ...manualForm, status: e.target.value })}
                                        required
                                        className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all text-neutral-700 dark:text-neutral-300"
                                    >
                                        <option value="present">Present</option>
                                        <option value="absent">Absent</option>
                                        <option value="late">Late</option>
                                        <option value="half-day">Half Day</option>
                                    </select>
                                </div>
                            </div>

                            {manualForm.fromDate !== manualForm.toDate && (
                                <p className="rounded-lg bg-[var(--accent-light)] px-3 py-2 text-xs font-semibold text-[var(--heading-secondary)] dark:bg-[var(--accent-subtle)] dark:text-[var(--heading-secondary)]">
                                    The selected times are applied separately to every date in this range; they are not one continuous multi-day shift.
                                </p>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Mode</label>
                                    <select
                                        value={manualForm.mode}
                                        onChange={(e) => setManualForm({ ...manualForm, mode: e.target.value })}
                                        required
                                        className="h-11 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-4 outline-none focus:border-[var(--accent-primary)] focus:ring-1 focus:ring-[var(--accent-primary)] text-sm font-medium transition-all text-neutral-700 dark:text-neutral-300"
                                    >
                                        <option value="office">Office</option>
                                        <option value="wfh">WFH</option>
                                    </select>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Check-In Time</label>
                                    <CustomTimePicker
                                        value={manualForm.checkIn}
                                        onChange={(e) => setManualForm({ ...manualForm, checkIn: e.target.value })}
                                        placeholder="09:30 AM"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Lunch Start</label>
                                    <CustomTimePicker
                                        value={manualForm.breakStart}
                                        onChange={(e) => setManualForm({ ...manualForm, breakStart: e.target.value })}
                                        placeholder="01:30 PM"
                                    />
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Lunch End</label>
                                    <CustomTimePicker
                                        value={manualForm.breakEnd}
                                        onChange={(e) => setManualForm({ ...manualForm, breakEnd: e.target.value })}
                                        placeholder="02:15 PM"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col gap-1.5">
                                    <label className="text-xs font-bold text-neutral-500 uppercase">Check-Out Time</label>
                                    <CustomTimePicker
                                        value={manualForm.checkOut}
                                        onChange={(e) => setManualForm({ ...manualForm, checkOut: e.target.value })}
                                        placeholder="06:30 PM"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-end gap-3 pt-6 border-t border-neutral-100 dark:border-neutral-800 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setIsManualModalOpen(false)}
                                    className="px-6 py-2.5 rounded-lg bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-semibold transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={manualSaving}
                                    className="px-8 py-2.5 rounded-lg bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white font-semibold transition-all shadow-lg shadow-[var(--accent-primary)]/20 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    {manualSaving ? "Saving..." : "Add Attendance"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* SELFIE / CHECK-IN PROOF PREVIEW MODAL */}
            {proofModalData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-[var(--card-bg)] border border-[var(--border-color)] shadow-2xl">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between border-b border-[var(--border-color)] px-6 py-4">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-[var(--accent-primary)]/10 border border-[var(--accent-primary)]/20 flex items-center justify-center text-[var(--accent-primary)]">
                                    <Camera size={20} />
                                </div>
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-base font-black text-[var(--text-primary)]">
                                            {proofModalData.employee?.name || "Employee"} Check-in Proof
                                        </h3>
                                        {proofModalData.salesCheckInProof?.source === 'selfie' && (
                                            <span className="rounded-full bg-[var(--accent-subtle)] text-[var(--accent-primary)] dark:text-[var(--accent-primary)] px-2 py-0.5 text-[10px] font-black uppercase tracking-wider border border-[var(--accent-primary)]/20">
                                                Selfie
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-[var(--text-secondary)]">
                                        {proofModalData.employee?.employeeId ? `${proofModalData.employee.employeeId} • ` : ""}{formatTeamName(proofModalData.employee?.team)}
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setProofModalData(null)}
                                className="rounded-xl p-2 text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] hover:text-[var(--text-primary)] transition-colors"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
                            {/* Photo display */}
                            <div className="relative overflow-hidden rounded-2xl border border-[var(--border-color)] bg-black/5 dark:bg-black/40 flex items-center justify-center">
                                <img
                                    src={proofModalData.salesCheckInProof?.photoData}
                                    alt="Check-in proof"
                                    className="max-h-[360px] w-full object-contain"
                                />
                            </div>

                            {/* Check-in Time */}
                            <div className="rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] p-3">
                                <span className="text-[10px] font-bold uppercase tracking-wider text-[var(--text-secondary)]">Check-in Time</span>
                                <p className="mt-1 text-sm font-bold text-[var(--text-primary)] flex items-center gap-1.5">
                                    <Clock size={15} className="text-[var(--accent-primary)]" />
                                    <span>
                                        {formatAttendanceRecordTime(proofModalData, 'checkIn') !== "-"
                                            ? formatAttendanceRecordTime(proofModalData, 'checkIn')
                                            : (proofModalData.salesCheckInProof?.capturedAt
                                                ? new Date(proofModalData.salesCheckInProof.capturedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })
                                                : "—")}
                                    </span>
                                </p>
                            </div>

                            {/* Location & GPS Info */}
                            {proofModalData.salesCheckInProof?.location?.latitude && proofModalData.salesCheckInProof?.location?.longitude ? (
                                <div className="rounded-xl bg-[var(--accent-subtle)]/50 border border-[var(--accent-primary)]/15 p-3.5 space-y-2">
                                    <div className="flex items-center gap-1.5 text-xs font-black text-[var(--heading-secondary)] dark:text-[var(--heading-secondary)]">
                                        <MapPin size={14} className="text-[var(--accent-primary)] dark:text-[var(--accent-primary)]" />
                                        <span>Check-in Location</span>
                                    </div>
                                    <p className="text-xs font-semibold text-[var(--text-primary)] leading-relaxed">
                                        <ReverseGeocode
                                            latitude={proofModalData.salesCheckInProof.location.latitude}
                                            longitude={proofModalData.salesCheckInProof.location.longitude}
                                        />
                                    </p>
                                    <div className="flex items-center justify-between pt-1">
                                        <span className="font-mono text-[11px] text-[var(--text-secondary)]">
                                            GPS: {Number(proofModalData.salesCheckInProof.location.latitude).toFixed(6)}, {Number(proofModalData.salesCheckInProof.location.longitude).toFixed(6)}
                                        </span>
                                        <a
                                            href={`https://www.google.com/maps?q=${proofModalData.salesCheckInProof.location.latitude},${proofModalData.salesCheckInProof.location.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-xs font-bold text-rose-600 hover:text-rose-700 underline"
                                        >
                                            <span>Open in Google Maps</span>
                                            <ExternalLink size={11} />
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-xl bg-neutral-50 dark:bg-neutral-800/40 border border-[var(--border-color)] p-3 text-xs text-[var(--text-secondary)]">
                                    GPS coordinates not recorded for this check-in.
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className="flex items-center justify-end border-t border-[var(--border-color)] px-6 py-3 bg-[var(--bg-primary)]">
                            <button
                                type="button"
                                onClick={() => setProofModalData(null)}
                                className="rounded-xl bg-[var(--accent-primary)] px-5 py-2 text-xs font-bold text-white shadow-md hover:bg-[var(--accent-hover)] transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

const InfoTile = ({ label, value, tone = "" }) => (
    <div className="min-w-0 rounded-xl bg-[var(--bg-primary)] px-3 py-2">
        <p className="text-[9px] font-black uppercase tracking-wide text-[var(--text-secondary)]">{label}</p>
        <p className={`mt-1 break-words text-xs font-bold ${tone || "text-[var(--text-primary)]"}`}>
            {value || "-"}
        </p>
    </div>
);

export default AttendanceReport;



